import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useSearch } from 'wouter';
import { activeAliases } from '@shared/productAliases';
import { PRODUCT_UNIT_MAX, existingSpellingOf, normalizeProductUnit, productUnitChoices } from '@shared/productUnit';
import { apiRequest, getApiPath } from '@/lib/queryClient';
import { apiErrorMessage } from "@/lib/apiErrorMessage";
import { aliasesTextToArray, productPatchBody } from '@/lib/productRequestBody';
import { parseApiError } from '@/components/pin-setup-code-step';
import { useToast } from '@/hooks/use-toast';
import { 
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import { 
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// UI Components
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DialogTrigger } from '@/components/ui/dialog';
import { CategoryIcon, getCategoryIconOptions } from '@/components/category-icon';

// Icons
import {
  Plus,
  Edit,
  Trash2,
  Settings as SettingsIcon,
  GripVertical,
  ChevronDown,
  ChevronUp,
  Camera,
  History,
  Search,
  Filter,
  Key,
  Image as ImageIcon,
  Loader2,
  Clock,
  Calendar as CalendarIcon,
  Tablet,
  HelpCircle,
  Truck,
  MapPin,
  Package,
  Link2,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { AUDIT_ACTION_LABELS, auditActionLabel } from '@/lib/auditActions';

// Components
import { QuickBooksAuth } from '@/components/quickbooks-auth';
import { QuickBooksSync } from '@/components/quickbooks-sync';
import { PickSheetSettingsCard } from '@/components/pick-sheet-settings-card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { DeliveryZonesCard } from '@/components/delivery-zones-card';
import { CustomerTypesCard } from '@/components/customer-types-card';
import { ProductCustomerTypesSection } from '@/components/product-customer-types';
import {
  CUSTOMER_TYPES_PATH,
  customerTypePath,
  pendingTypeTicks,
  productCustomerTypePath,
  type CustomerTypeDetail,
  type ProductTypeTick,
} from '@/lib/customerTypes';
import {
  PRICE_CELL_CLASS,
  TypePriceCell,
  TypePriceHeader,
  TypePriceSaveBar,
  movePriceFocus,
  useTypePriceGrid,
  type TypePriceGrid,
} from '@/components/customer-type-price-grid';
import { ProductUnitsSection } from '@/components/product-units-section';
import { categoryReorderList, unitsOfItem } from '@/lib/itemUnits';
import { groupProductSizes, sizeUnitSuffix } from '@shared/productSizes';
import { ProductNamePreview, type PreviewMember } from '@/components/product-name-preview';
import { ITEM_NAME_MAX, PACK_MAX, nextProductName, productParts, tidyNamePart } from '@shared/productNames';
import { EVERY_DAY, describeActiveWindow, shortTime } from '@shared/orderAlertWindow';
import { hiddenInactiveIds, readHideInactive, reorderCategoryIds, writeHideInactive } from '@/lib/masterMenuFilter';
import { productActiveRequest, withProductActive } from '@/lib/productActiveSwitch';
import { useOrganization } from '@/contexts/OrganizationContext';

// Professional potato images - specific varieties
import potatoRusset from "../assets/potato-new.png"; // Russet/White potatoes
import potatoYellow from "../assets/potato-yellow.png"; // Yellow/Yukon varieties
import potatoSmall from "../assets/potato-small.png"; // Small/fingerling potatoes  
import potatoSweet from "../assets/potato-sweet.png"; // Sweet potatoes
import potatoVariety from "../assets/potato-variety.png"; // Red and mixed varieties

// QuickBooks logo for system category
import quickbooksLogo from "@assets/justIntuit_QuickBooks_logo_1765386143971.png";

// Optimized 300x225 potato images (24-35KB each)
import potato90ctBaking from "@assets/90ct Baking Potato.jpg";
import potato50lbFingerling from "@assets/50lb Fingerling.jpg";
import potato25lbFingerling from "@assets/25lb Fingerling.jpg";
import purpleYam from "@assets/Purple Yam.jpg";
import potato10lbRed from "@assets/10lb Red Potato.jpg";
import potato10lbWhite from "@assets/10lb White Potato.jpg";
import potato10lbYukon from "@assets/10lb Yukon Potato.jpg";
import potato10lbRusset from "@assets/10lb Russet Potato.jpg";
import potato50lbRed from "@assets/50lb Red Potato.jpg";
import potato50lbYukon from "@assets/50lb Yukon Potato.jpg";
import potato50lbWhite from "@assets/50lb White Potato.jpg";
import potatoARed from "@assets/A Red Potato.jpg";
import potatoBRed from "@assets/B Red Potato.jpg";
import potatoCRed from "@assets/C Red Potato.jpg";
// Additional optimized potato images
import potato70ctBaking from "@assets/70ct Baking Potato.jpg";
import potatoCYellow from "@assets/C Yellow Potato.jpg";
import potatoBYellow from "@assets/B Yellow Potato.jpg";
import potatoAYellow from "@assets/A Yellow Potato.jpg";
import potatoSweetPotato from "@assets/Sweet Potato.jpg";
import potatoTrio from "@assets/Trio.jpg";
import potatoParisian from "@assets/Parisian Potato.jpg";
import potatoCWhite from "@assets/C White Potato.jpg";
import potatoBWhite from "@assets/B White Potato.jpg";
import potatoAWhite from "@assets/A White Potato.jpg";
import potato3L from "@assets/3L Potato.jpg";

// Default product image fallback
import defaultProductLogo from "@assets/Logo_-new_clear_1764787161621.png";
import { SupportActorMark } from "@/components/support-actor-mark";

// The item form's hover tip for the item's name.
const ITEM_TIP = "The name customers see on your menu, like Lemons or Navel Oranges 48s. Each unit it is sold by prints as its own line: Lemons - case, Lemons - each.";

// Validation schemas
const productSchema = z.object({
  // The item; the server joins it and the unit into the official name
  // (shared/productNames.ts), and the form shows that as "Prints as".
  itemName: z.string().trim().min(1, "Item is required").max(ITEM_NAME_MAX, `Keep the item to ${ITEM_NAME_MAX} characters`),
  // An old row's pack, sent back as stored until the admin removes it. No box of its own.
  pack: z.string().max(PACK_MAX, `Keep the pack or size to ${PACK_MAX} characters`).optional(),
  categoryId: z.number().min(1, "Category is required"),
  isActive: z.boolean().default(true),
  imagePath: z.string().optional(),
  subheading: z.string().optional(),
  unit: z.string().max(PRODUCT_UNIT_MAX, `Keep the unit to ${PRODUCT_UNIT_MAX} characters`).optional(),
  aliases: z.string().optional(), // comma-separated in the form; sent to the API as an array
  price: z.string().optional(),
  taxPercent: z.string().optional(),
});

const categorySchema = z.object({
  name: z.string().min(1, "Category name is required"),
  icon: z.string().min(1, "Icon is required"),
  isVisibleToCustomers: z.boolean().default(true),
});

const accountSchema = z.object({
  name: z.string().min(1, "Account name is required"),
  phone: z.string().min(10, "Phone number must be 10 digits").regex(/^\d{3}-\d{3}-\d{4}$/, "Format: XXX-XXX-XXXX"),
  customUrl: z.string().min(1, "Custom URL is required"),
  isActive: z.boolean(),
  customMenu: z.object({
    categories: z.record(z.boolean()),
    products: z.record(z.boolean())
  }).optional()
});

const userSchema = z.object({
  username: z.string().min(1, "Display name is required").min(3, "Display name must be at least 3 characters"),
  email: z.string().min(1, "Email is required").email("Invalid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
  role: z.enum(["admin", "staff"]),
  isActive: z.boolean(),
});

const userEditSchema = z.object({
  username: z.string().min(1, "Display name is required").min(3, "Display name must be at least 3 characters"),
  email: z.string().min(1, "Email is required").email("Invalid email address"),
  role: z.enum(["admin", "staff"]),
  isActive: z.boolean(),
});

const passwordResetSchema = z.object({
  newPassword: z.string().min(6, "Password must be at least 6 characters"),
});

type ProductForm = z.infer<typeof productSchema>;
// What a product save carries: the dialog's fields plus the name they make, or — the
// drag-and-drop category move — the product's name as it stands and no parts.
type ProductSave = Omit<ProductForm, "itemName"> & { itemName?: string; name: string };
type CategoryForm = z.infer<typeof categorySchema>;
type AccountForm = z.infer<typeof accountSchema>;
type UserForm = z.infer<typeof userSchema>;
type UserEditForm = z.infer<typeof userEditSchema>;
type PasswordResetForm = z.infer<typeof passwordResetSchema>;

interface Product {
  id: number;
  name: string;
  itemName?: string | null;
  pack?: string | null;
  categoryId: number;
  isActive: boolean;
  imagePath?: string;
  subheading?: string;
  unit?: string | null;
  aliases?: unknown; // ProductAlias[] json — always read through normalizeProductAliases/activeAliases
  quickbooksItemId?: string | null;
  price?: string | null;
  taxPercent?: string | null;
  sizeOfProductId?: number | null; // a linked size of this product (shared/productSizes.ts)
  caseFraction?: string | null;
  unitOrder?: number | null; // its place among its item's units, once ordered
}

interface Category {
  id: number;
  name: string;
  icon: string;
  isVisibleToCustomers?: boolean;
  isSystemCategory?: boolean;
}

interface User {
  id: number;
  username: string;
  email?: string;
  role: string;
  isActive: boolean;
  createdAt: string;
  lastLoginAt?: string;
}

interface OrderWindowConfig {
  id: number;
  organizationId: number;
  enabled: boolean;
  frequency: string;
  timezone: string;
  anchorStart: string;
  cutoffHour: number;
  cutoffMinute: number;
  customDays: number;
  deliveryEnabled?: boolean;
  pickupEnabled?: boolean;
  pickupFollowsDeliverySchedule?: boolean;
  pickupSchedulingEnabled?: boolean;
  autoChangeNotes?: boolean;
  deliveryDaysByZone?: boolean;
  createdAt: string;
  updatedAt: string;
}

// Sortable Category Component
function SortableCategory({ 
  category, 
  products, 
  hiddenCount = 0,
  isOpen, 
  onToggle, 
  onEditCategory, 
  onDeleteCategory, 
  onEditProduct, 
  onDeleteProduct,
  onPriceChange,
  onActiveChange,
  getProductImage,
  typeGrid,
}: {
  category: Category;
  /** The rows to draw. */
  products: Product[];
  /** Inactive products "Hide inactive items" left out of `products`. */
  hiddenCount?: number;
  isOpen: boolean;
  onToggle: () => void;
  onEditCategory: (category: Category) => void;
  onDeleteCategory: (id: number) => void;
  onEditProduct: (product: Product) => void;
  onDeleteProduct: (id: number) => void;
  onPriceChange: (id: number, price: string) => void;
  onActiveChange: (id: number, isActive: boolean) => void;
  getProductImage: (productName: string) => string;
  /** Customer-type price columns; inactive (no types) renders today's rows. */
  typeGrid?: TypePriceGrid;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: `category-${category.id}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const categoryProducts = products.filter(p => p.categoryId === category.id);
  // An item sold by several units is one row with its units under it (lib/itemUnits.ts).
  const items = groupProductSizes(categoryProducts);

  return (
    <div ref={setNodeRef} style={style} className="bg-white border-y border-x-0 md:border md:border-cn-warm border-cn-warm rounded-none md:rounded-lg">
      <Collapsible open={isOpen} onOpenChange={onToggle}>
        <div className="flex items-center justify-between p-2 md:p-4 hover:bg-cn-leaf/20 transition-colors">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <div {...attributes} {...listeners} className="cursor-grab hover:cursor-grabbing p-1">
              <GripVertical className="h-5 w-5 text-gray-400" />
            </div>

            {/* Icon — tapping toggles collapse */}
            <CollapsibleTrigger className="flex-shrink-0">
              {category.isSystemCategory || category.icon === "quickbooks" ? (
                <img src={quickbooksLogo} alt="QuickBooks" className="w-8 h-8 rounded" />
              ) : (
                <CategoryIcon icon={category.icon} size="lg" />
              )}
            </CollapsibleTrigger>

            {/* Name — tapping opens edit dialog */}
            <div
              className="text-left min-w-0 cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1 transition-colors"
              onClick={() => onEditCategory(category)}
            >
              <h3 className="font-semibold text-cn-navy truncate">{category.name}</h3>
              <p className="text-sm text-gray-600">
                {items.length} items
                {hiddenCount > 0 && (
                  <span className="text-gray-400" data-testid={`text-category-hidden-count-${category.id}`}> · {hiddenCount} inactive hidden</span>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-1 ml-2 flex-shrink-0">
            {category.isVisibleToCustomers === false && (
              <span className="bg-amber-100 text-amber-700 p-1 h-auto text-xs rounded">
                Hidden
              </span>
            )}
            {/* Edit button — desktop only */}
            <Button
              variant="ghost"
              size="sm"
              className="hidden md:inline-flex"
              onClick={(e) => {
                e.stopPropagation();
                onEditCategory(category);
              }}
              data-testid={`button-edit-category-${category.id}`}
            >
              <Edit className="h-4 w-4" />
            </Button>
            {/* Chevron — always on far right */}
            <CollapsibleTrigger className="flex-shrink-0 p-2">
              {isOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </CollapsibleTrigger>
          </div>
        </div>
        <CollapsibleContent>
          <div className="border-t border-cn-warm">
            <SortableContext 
              items={items.map(({ head }) => `product-${head.id}`)}
              strategy={verticalListSortingStrategy}
            >
              <div className="ml-0 md:ml-8">
                {typeGrid?.active && categoryProducts.length > 0 && <TypePriceHeader columns={typeGrid.columns} />}
                {items.map(({ head, members }) =>
                  members.length > 1 ? (
                    <SortableItemGroup
                      key={head.id}
                      head={head}
                      units={members}
                      onEdit={onEditProduct}
                      onDelete={onDeleteProduct}
                      onPriceChange={onPriceChange}
                      onActiveChange={onActiveChange}
                      getProductImage={getProductImage}
                      typeGrid={typeGrid}
                    />
                  ) : (
                    <SortableProduct
                      key={head.id}
                      product={head}
                      onEdit={onEditProduct}
                      onDelete={onDeleteProduct}
                      onPriceChange={onPriceChange}
                      onActiveChange={onActiveChange}
                      getProductImage={getProductImage}
                      typeGrid={typeGrid}
                    />
                  ),
                )}
              </div>
            </SortableContext>
            {categoryProducts.length === 0 && (
              <div className="p-4 text-center text-gray-500 ml-8">
                {hiddenCount > 0
                  ? `Every item in this category is inactive (${hiddenCount}). Untick "Hide inactive items" to see ${hiddenCount === 1 ? 'it' : 'them'}.`
                  : 'No products in this category'}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

// A product row has no pencil: its name opens the editor, by click or by Enter / Space.
function openOnActivate(open: () => void, label: string, testId: string) {
  return {
    role: 'button',
    tabIndex: 0,
    'aria-label': label,
    'data-testid': testId,
    onClick: open,
    onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        open();
      }
    },
  };
}

// An item sold by several units: the item's row — dragged as one, opening the item form —
// and under it one row per unit, each with its own price.
function SortableItemGroup({
  head,
  units,
  onEdit,
  onDelete,
  onPriceChange,
  onActiveChange,
  getProductImage,
  typeGrid,
}: {
  head: Product;
  /** The item's units as the customer's card lists them (groupProductSizes). */
  units: Product[];
  onEdit: (product: Product) => void;
  onDelete: (id: number) => void;
  onPriceChange: (id: number, price: string) => void;
  onActiveChange: (id: number, isActive: boolean) => void;
  getProductImage: (productName: string, product?: any) => string;
  typeGrid?: TypePriceGrid;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: `product-${head.id}` });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const anyActive = units.some((unit) => unit.isActive);
  const { item } = productParts(head);
  return (
    <div ref={setNodeRef} style={style} className="border-b border-cn-warm last:border-b-0" data-testid={`row-item-${head.id}`}>
      <div className="flex items-center justify-between p-2 md:p-4 md:pb-2 hover:bg-[var(--neutral)]">
        <div className="flex items-center space-x-3 min-w-0 flex-1">
          <div {...attributes} {...listeners} className="cursor-grab hover:cursor-grabbing">
            <GripVertical className="h-4 w-4 text-gray-400" />
          </div>
          <img
            src={getProductImage(head.name, head)}
            alt={item}
            className={`w-10 h-10 object-cover rounded flex-shrink-0 ${anyActive ? '' : 'opacity-40 grayscale'}`}
          />
          {/* No pencil (Jon, 2026-10-14): the item's name opens the item page. */}
          <div
            className="min-w-0 cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            {...openOnActivate(() => onEdit(head), `Edit ${item}`, `button-edit-item-${head.id}`)}
          >
            <p className={`font-medium truncate ${anyActive ? 'text-cn-navy' : 'text-gray-500'}`}>{item}</p>
            <p className="text-sm text-gray-600">
              {units.length} units{!anyActive && ' · all inactive'}
            </p>
          </div>
        </div>
      </div>
      {units.map((unit) => (
        <SortableProduct
          key={unit.id}
          product={unit}
          asUnit
          onEdit={onEdit}
          onDelete={onDelete}
          onPriceChange={onPriceChange}
          onActiveChange={onActiveChange}
          getProductImage={getProductImage}
          typeGrid={typeGrid}
        />
      ))}
    </div>
  );
}

// Sortable Product Component: an item sold by one unit, or (asUnit) one unit row under
// its item's row in SortableItemGroup — not dragged on its own, its item moves it.
function SortableProduct({ 
  product, 
  asUnit = false,
  onEdit, 
  onDelete, 
  onPriceChange,
  onActiveChange,
  getProductImage,
  typeGrid,
}: {
  product: Product;
  asUnit?: boolean;
  onEdit: (product: Product) => void;
  onDelete: (id: number) => void;
  onPriceChange: (id: number, price: string) => void;
  onActiveChange: (id: number, isActive: boolean) => void;
  getProductImage: (productName: string, product?: any) => string;
  typeGrid?: TypePriceGrid;
}) {
  // With customer types the row becomes a price grid: base + one column per type.
  const grid = typeGrid?.active ? typeGrid : null;
  const [localPrice, setLocalPrice] = useState(product.price || '');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setLocalPrice(product.price || '');
  }, [product.price]);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: `product-${product.id}`, disabled: asUnit });

  const style = asUnit ? undefined : {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const { item, pack } = productParts(product);
  
  const handlePriceBlur = () => {
    const trimmedPrice = localPrice.trim();
    if (trimmedPrice !== (product.price || '')) {
      setIsSaving(true);
      onPriceChange(product.id, trimmedPrice);
      setTimeout(() => setIsSaving(false), 500);
    }
  };
  
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Moving to another cell blurs this one, which saves it.
    if (grid && movePriceFocus(e)) return;
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
  };

  // The same three controls in both layouts; with customer types the prices move to
  // the right edge so the column headers line up.
  const priceInput = (
    <Input
      type="text"
      inputMode="decimal"
      value={localPrice}
      onChange={(e) => setLocalPrice(e.target.value)}
      onBlur={handlePriceBlur}
      onKeyDown={handleKeyDown}
      placeholder="—"
      {...(grid ? { 'aria-label': 'Price', 'data-price-col': 'base', 'data-price-row': product.id } : {})}
      className={`price-input w-14 md:w-16 h-7 text-sm text-right px-1 ${isSaving ? 'bg-green-50 border-green-300' : ''}`}
    />
  );
  // The badge is a switch: pick the other value and it saves, without opening the form.
  // Both values are one width, so switching never moves the price beside it.
  const statusBadge = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          aria-label={`${product.name}: ${product.isActive ? 'Active' : 'Inactive'}. Change status`}
          className={`hidden md:inline-flex ${
            product.isActive
              ? "bg-[var(--status-procured-bg)] text-cn-brand-green hover:bg-[var(--status-procured-bg)] hover:brightness-95"
              : "bg-[var(--warning-light)] text-[var(--warning)] hover:bg-[var(--warning-light)] hover:brightness-95"
          } min-w-[4.25rem] gap-0.5 p-1 h-auto text-xs [&_svg]:size-3`}
          data-testid={`button-status-product-${product.id}`}
        >
          {product.isActive ? "Active" : "Inactive"}
          <ChevronDown aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[8rem]">
        <DropdownMenuRadioGroup
          value={product.isActive ? 'active' : 'inactive'}
          onValueChange={(value) => {
            const isActive = value === 'active';
            if (isActive !== product.isActive) onActiveChange(product.id, isActive);
          }}
        >
          <DropdownMenuRadioItem value="active" data-testid={`menu-status-active-${product.id}`}>Active</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="inactive" data-testid={`menu-status-inactive-${product.id}`}>Inactive</DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
  // No pencil on any product row (Jon, 2026-10-14): the row's name opens the editor.
  // On desktop a unit row's text starts under its item's name (Jon, 2026-10-14): the item
  // row's padding, grip, picture and gaps come to 100px (measured in the browser).
  return (
    <div
      ref={asUnit ? undefined : setNodeRef}
      style={style}
      className={asUnit
        ? "flex items-center justify-between py-1.5 pr-2 pl-9 md:py-2 md:pr-4 md:pl-[6.25rem] hover:bg-[var(--neutral)]"
        : "flex items-center justify-between p-2 md:p-4 border-b border-cn-warm last:border-b-0 hover:bg-[var(--neutral)]"}
      data-testid={asUnit ? `row-unit-${product.id}` : undefined}
    >
      <div className="flex items-center space-x-3 min-w-0 flex-1">
        {!asUnit && (
        <div {...attributes} {...listeners} className="cursor-grab hover:cursor-grabbing">
          <GripVertical className="h-4 w-4 text-gray-400" />
        </div>
        )}
        {/* Below md the Active / Inactive badge is not drawn, so an inactive row says so on
            its thumbnail: with two customer-type columns a 375px screen leaves the name
            about 39px, and the thumbnail is the one slot whose width never changes. */}
        {!asUnit && (
        <div className="relative flex-shrink-0">
          <img
            src={getProductImage(product.name, product)}
            alt={product.name}
            className={`w-10 h-10 object-cover rounded ${product.isActive ? '' : 'max-md:opacity-40 max-md:grayscale'}`}
          />
          {!product.isActive && (
            <span
              className="md:hidden absolute -inset-x-1 -bottom-1 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold leading-[14px] text-center whitespace-nowrap"
              data-testid={`badge-inactive-product-${product.id}`}
            >
              Inactive
            </span>
          )}
        </div>
        )}
        <div
          className={`min-w-0 cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring${
            asUnit ? ' flex-1' : ''
          }`}
          {...openOnActivate(
            () => onEdit(product),
            `Edit ${product.name}`,
            asUnit ? `button-edit-unit-${product.id}` : `button-edit-product-${product.id}`,
          )}
        >
          <p className={`font-medium text-cn-navy truncate ${product.isActive ? '' : 'max-md:text-gray-500'}`}>
            {asUnit ? (
              <>
                {/* Plain text here; the customer's menu keeps the tag (Jon, 2026-10-14). */}
                <span data-testid={`text-unit-${product.id}`}>{product.unit ? sizeUnitSuffix(product.unit) : 'no unit'}</span>
                {pack && <span className="ml-1.5 text-sm font-normal text-gray-500">{pack}</span>}
                {!product.isActive && <span className="md:hidden ml-1.5 text-xs font-normal text-amber-700">Inactive</span>}
              </>
            ) : (
              <>
                {item}
                {pack && <span className="ml-1.5 text-sm font-normal text-gray-500">{pack}</span>}
                {product.unit && (
                  <span className="ml-1.5 text-sm font-normal text-gray-500" data-testid={`text-unit-${product.id}`}>
                    {sizeUnitSuffix(product.unit)}
                  </span>
                )}
              </>
            )}
            {grid && product.taxPercent && (
              <span className="ml-1 text-xs font-normal text-orange-600" title={`${product.taxPercent}% tax`}>+tax</span>
            )}
          </p>
          {product.subheading && (
            <p className={`text-sm text-gray-600 truncate ${product.isActive ? '' : 'max-md:text-gray-500'}`}>{product.subheading}</p>
          )}
        </div>
      </div>
      {grid ? (
        <div className="flex items-center gap-2 flex-shrink-0">
          {statusBadge}
          <div className={`${PRICE_CELL_CLASS} price-input-wrap`}>
            <span className="text-gray-500 text-sm mr-1">$</span>
            {priceInput}
          </div>
          {grid.columns.map((column) => (
            <TypePriceCell key={column.id} grid={grid} column={column} product={product} />
          ))}
        </div>
      ) : (
        <div className="flex items-center space-x-2 flex-shrink-0">
          <div className="flex items-center flex-shrink-0 price-input-wrap">
            <span className="text-gray-500 text-sm mr-1">$</span>
            {priceInput}
            {product.taxPercent && (
              <span 
                className="ml-1 text-xs text-orange-600 cursor-pointer" 
                title={`${product.taxPercent}% tax`}
                onClick={() => onEdit(product)}
              >
                +tax
              </span>
            )}
          </div>
          {statusBadge}
        </div>
      )}
    </div>
  );
}

// History Tab Component
type AuditLog = {
  id: number;
  entityType: string;
  entityId: number;
  action: string;
  oldValue: string | null;
  newValue: string | null;
  metadata: {
    accountId?: number;
    accountName?: string;
    orderId?: number;
    productId?: number;
    productName?: string;
    userId?: number;
    username?: string;
    actor?: 'super_admin';
  } | null;
  createdAt: string;
};

function HistoryTab() {
  const [productFilter, setProductFilter] = useState('');
  const [accountFilter, setAccountFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('all');

  const { data: logs, isLoading } = useQuery<AuditLog[]>({
    queryKey: ['/api/audit-logs'],
    refetchInterval: 10000, // Auto-refresh every 10 seconds
  });

  // Filter logs based on search criteria
  const filteredLogs = React.useMemo(() => {
    if (!logs) return [];
    
    return logs.filter(log => {
      // Exclude FreshOrder's own entries (impersonation, add-on switches)
      if (log.action.startsWith('super_admin_')) {
        return false;
      }
      
      // Product name filter
      if (productFilter && !log.metadata?.productName?.toLowerCase().includes(productFilter.toLowerCase())) {
        return false;
      }
      
      // Account name filter
      if (accountFilter && !log.metadata?.accountName?.toLowerCase().includes(accountFilter.toLowerCase())) {
        return false;
      }
      
      // Action type filter
      if (actionFilter !== 'all' && log.action !== actionFilter) {
        return false;
      }
      
      return true;
    });
  }, [logs, productFilter, accountFilter, actionFilter]);

  const getActionBadge = (action: string) => {
    switch (action) {
      case 'quantity_changed':
        return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Quantity Changed</Badge>;
      case 'procurement_reset':
        return <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">Procurement Reset</Badge>;
      default:
        return <Badge variant="secondary">{auditActionLabel(action)}</Badge>;
    }
  };

  return (
    <Card>
      <CardHeader className="border-b border-gray-100 bg-gray-50/50">
        <div className="flex flex-col gap-4">
          <div>
            <CardTitle>Order Change History</CardTitle>
            <CardDescription>
              Complete audit trail of order item modifications
            </CardDescription>
          </div>
          
          {/* Filters in header */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Filter by product..."
                value={productFilter}
                onChange={(e) => setProductFilter(e.target.value)}
                className="pl-9"
                data-testid="input-product-filter"
              />
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Filter by account..."
                value={accountFilter}
                onChange={(e) => setAccountFilter(e.target.value)}
                className="pl-9"
                data-testid="input-account-filter"
              />
            </div>
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger data-testid="select-action-filter">
                <SelectValue placeholder="All actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {Object.entries(AUDIT_ACTION_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {(productFilter || accountFilter || actionFilter !== 'all') && (
            <div className="flex items-center gap-2">
              <Badge variant="secondary">
                {filteredLogs.length} result{filteredLogs.length !== 1 ? 's' : ''}
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setProductFilter('');
                  setAccountFilter('');
                  setActionFilter('all');
                }}
                data-testid="button-clear-filters"
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
          {isLoading ? (
            <div className="text-center py-12 text-muted-foreground">
              Loading history...
            </div>
        ) : !filteredLogs || filteredLogs.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            {logs && logs.length > 0 ? 'No results match your filters' : 'No changes recorded yet'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date & Time</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead>Old Value</TableHead>
                  <TableHead>New Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLogs.map((log) => (
                  <TableRow key={log.id} data-testid={`row-log-${log.id}`}>
                    <TableCell className="font-medium">
                      {format(new Date(log.createdAt), 'MMM dd, yyyy h:mm a')}
                    </TableCell>
                    <TableCell>
                      {getActionBadge(log.action)}
                      <SupportActorMark actor={log.metadata?.actor} />
                    </TableCell>
                    <TableCell>
                      <div>{log.metadata?.productName || '-'}</div>
                      {log.metadata?.orderId && (
                        <div className="text-sm text-muted-foreground">Order #{log.metadata.orderId}</div>
                      )}
                    </TableCell>
                    <TableCell>{log.metadata?.accountName || '-'}</TableCell>
                    <TableCell className="text-red-600 font-medium max-w-xs">
                      <div className="line-clamp-3 whitespace-pre-wrap break-words" title={log.oldValue ?? undefined}>
                        {log.oldValue !== null ? log.oldValue : '-'}
                      </div>
                    </TableCell>
                    <TableCell className="text-green-600 font-medium max-w-xs">
                      <div className="line-clamp-3 whitespace-pre-wrap break-words" title={log.newValue ?? undefined}>
                        {log.newValue !== null ? log.newValue : '-'}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function groupConsecutiveDates(dates: string[]): { start: string; end: string; dates: string[] }[] {
  if (dates.length === 0) return [];
  const sorted = [...dates].sort();
  const groups: { start: string; end: string; dates: string[] }[] = [];
  let currentGroup = { start: sorted[0], end: sorted[0], dates: [sorted[0]] };

  for (let i = 1; i < sorted.length; i++) {
    const prevDate = new Date(currentGroup.end + 'T12:00:00');
    const currDate = new Date(sorted[i] + 'T12:00:00');
    const diffDays = Math.round((currDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 1) {
      currentGroup.end = sorted[i];
      currentGroup.dates.push(sorted[i]);
    } else {
      groups.push(currentGroup);
      currentGroup = { start: sorted[i], end: sorted[i], dates: [sorted[i]] };
    }
  }
  groups.push(currentGroup);
  return groups;
}

function formatBlackoutChipLabel(group: { start: string; end: string; dates: string[] }): string {
  const fmt = (dateStr: string) => {
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };
  if (group.start === group.end) return fmt(group.start);
  const startD = new Date(group.start + 'T12:00:00');
  const endD = new Date(group.end + 'T12:00:00');
  const startMonth = startD.toLocaleDateString('en-US', { month: 'short' });
  const endMonth = endD.toLocaleDateString('en-US', { month: 'short' });
  if (startMonth === endMonth) {
    return `${startD.toLocaleDateString('en-US', { weekday: 'short' })}, ${startMonth} ${startD.getDate()}\u2013${endD.getDate()}`;
  }
  return `${fmt(group.start)} \u2013 ${fmt(group.end)}`;
}

const timeOptions = (() => {
  const options: { value: string; label: string }[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      const displayTime = new Date(`1970-01-01T${timeString}:00`).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      options.push({ value: timeString, label: displayTime });
    }
  }
  return options;
})();

// The "?" beside a label. It opens when the mouse passes over it, so it must never move
// keyboard focus. Left alone, a Radix Popover focuses its content when it opens and its
// trigger when it closes: a mouse that brushed a "?" on its way to a box took the caret
// out of the box being typed in, and a click into a box while the tip was closing ended
// with focus on the "?" — the typing that followed went nowhere.
// Declared out here, not inside Settings: a component declared during a render is a new
// component on every render, so each keystroke in a form on the page unmounted every tip.
// `inTabOrder={false}`: the tip beside a box that is filled by keyboard, where Tab
// should go box to box; that box carries the same words as its aria-description.
function InfoTooltip({ children, inTabOrder = true }: { children: React.ReactNode; inTabOrder?: boolean }) {
  const [open, setOpen] = React.useState(false);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout>>();
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span
          role="button"
          tabIndex={inTabOrder ? 0 : -1}
          className="inline-flex items-center"
          onMouseEnter={() => {
            clearTimeout(timeoutRef.current);
            setOpen(true);
          }}
          onMouseLeave={() => {
            timeoutRef.current = setTimeout(() => setOpen(false), 300);
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpen(prev => !prev);
          }}
          onKeyDown={(e) => {
            // A span is not a button to the browser: Enter and Space do not click it.
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setOpen(prev => !prev);
            }
          }}
        >
          <HelpCircle className="h-3.5 w-3.5 text-gray-400 cursor-help" />
        </span>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        className="max-w-xs p-3"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        // The mouse goes through the tip (index.css): with no room above its "?" a tip
        // opens over the box it describes, and a click there is meant for the box.
        data-help-tip=""
      >
        <p className="text-sm">{children}</p>
      </PopoverContent>
    </Popover>
  );
}

export default function Settings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const searchString = useSearch();
  const { organization, apiBasePath } = useOrganization();
  // Kiosk mode is a paid add-on FreshOrder switches on per vendor. Off: no tab,
  // no option in the mobile tab select, no panel. The kiosk routes refuse too.
  const kioskEnabled = organization?.kioskEnabled === true;
  
  // Get current user's role from session storage
  const currentUserRole = (() => {
    try {
      const adminUser = sessionStorage.getItem("adminUser");
      if (adminUser) {
        const parsed = JSON.parse(adminUser);
        return parsed.role || 'staff';
      }
    } catch (e) {}
    return 'staff';
  })();
  
  // Parse URL query params for tab selection
  const getTabFromUrl = () => {
    const params = new URLSearchParams(searchString);
    let tab = params.get('tab');
    // Integrations was merged into the Admin tab — keep old ?tab=integrations deep-links working.
    if (tab === 'integrations') tab = 'admin';
    if (tab && ['catalog', 'delivery', 'admin', 'history'].includes(tab)) {
      return tab;
    }
    return 'catalog';
  };
  
  const [activeTab, setActiveTab] = useState(getTabFromUrl);
  
  // Update tab when URL changes
  useEffect(() => {
    const tab = getTabFromUrl();
    setActiveTab(tab);
  }, [searchString]);

  // The kiosk tab is not there to sit on while kiosk mode is off.
  useEffect(() => {
    if (!kioskEnabled && activeTab === 'kiosk') setActiveTab('catalog');
  }, [kioskEnabled, activeTab]);
  
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  // The product form's "Shown to <type>" ticks the admin has changed, by type id. Saved
  // with the form; a fresh form (another product, or the dialog reopened) starts clean.
  const [productTypeChoices, setProductTypeChoices] = useState<Record<number, boolean>>({});
  React.useEffect(() => {
    setProductTypeChoices({});
  }, [productDialogOpen, editingProduct?.id]);
  const [deactivateConfirmState, setDeactivateConfirmState] = useState<{
    open: boolean;
    productId: number | null;
    productName: string;
    itemCount: number;
  }>({ open: false, productId: null, productName: '', itemCount: 0 });
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [pendingImagePreview, setPendingImagePreview] = useState<string | null>(null);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [openCategories, setOpenCategories] = useState<Set<number>>(new Set());
  // Master Menu: inactive products are not drawn unless this is unticked (remembered per browser).
  const [hideInactive, setHideInactive] = useState(readHideInactive);
  // Rows switched with their Active / Inactive badge since the filter was last ticked:
  // they stay drawn, so a row switched off does not vanish under the cursor (and can be
  // switched back). Ticking "Hide inactive items" again lets the filter take them.
  const [switchedInPlace, setSwitchedInPlace] = useState<Set<number>>(() => new Set());
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [createType, setCreateType] = useState<'product' | 'category'>('product');

  // QuickBooks product linking state
  const [qbProductLinkOpen, setQbProductLinkOpen] = useState(false);
  const [qbProductSearchQuery, setQbProductSearchQuery] = useState("");

  // Account management state
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<any>(null);
  const [menuConfigOpen, setMenuConfigOpen] = useState<Record<number, boolean>>({});
  const [statusPopoverOpen, setStatusPopoverOpen] = useState<number | null>(null);
  const [accountSortConfig, setAccountSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  // User management state
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [passwordResetDialogOpen, setPasswordResetDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null);

  // Kiosk mode settings state
  const [kioskPin, setKioskPin] = useState('');
  const [kioskTimeout, setKioskTimeout] = useState('60');
  const [showKioskPin, setShowKioskPin] = useState(false);
  const [autoChangeNotes, setAutoChangeNotes] = useState(true);
  const [deliveryDaysByZone, setDeliveryDaysByZone] = useState(false);

  // Order window settings state
  const [orderWindowEnabled, setOrderWindowEnabled] = useState(false);
  const [orderWindowFrequency, setOrderWindowFrequency] = useState('weekly');
  const [orderWindowCutoffHour, setOrderWindowCutoffHour] = useState(17);
  const [orderWindowCutoffMinute, setOrderWindowCutoffMinute] = useState(0);
  const [orderWindowCustomDays, setOrderWindowCustomDays] = useState(0);
  const [orderWindowAnchorDate, setOrderWindowAnchorDate] = useState<Date>(new Date());
  const [pickupSchedulingEnabled, setPickupSchedulingEnabled] = useState(false);
  const [pickupAvailableDays, setPickupAvailableDays] = useState(62); // Default Mon-Fri (bits 1-5)
  const [pickupBlackoutDates, setPickupBlackoutDates] = useState<string[]>([]);
  const [pickupMaxLeadDays, setPickupMaxLeadDays] = useState(7);
  const [deliveryLeadDays, setDeliveryLeadDays] = useState(1);
  const [deliveryMaxLeadDays, setDeliveryMaxLeadDays] = useState(7);
  const [deliveryBlackoutDates, setDeliveryBlackoutDates] = useState<string[]>([]);
  const [deliveryEnabled, setDeliveryEnabled] = useState(true);
  const [pickupEnabled, setPickupEnabled] = useState(false);
  const [pickupFollowsDeliverySchedule, setPickupFollowsDeliverySchedule] = useState(false);
  const [pickupCutoffHour, setPickupCutoffHour] = useState(17);
  const [pickupCutoffMinute, setPickupCutoffMinute] = useState(0);
  const [pickupPrepDays, setPickupPrepDays] = useState(0);
  const [deliveryBlackoutRangeStart, setDeliveryBlackoutRangeStart] = useState<Date | null>(null);
  const [pickupBlackoutRangeStart, setPickupBlackoutRangeStart] = useState<Date | null>(null);

  // Notification defaults state
  const [notifSmsEnabled, setNotifSmsEnabled] = useState(false);
  const [notifSmsTemplate, setNotifSmsTemplate] = useState('');
  const [notifSmsFirstDay, setNotifSmsFirstDay] = useState('monday');
  const [notifSmsFirstTime, setNotifSmsFirstTime] = useState('08:00');
  const [notifSmsSecondDay, setNotifSmsSecondDay] = useState('wednesday');
  const [notifSmsSecondTime, setNotifSmsSecondTime] = useState('08:00');
  const [notifSmsOrderReceipts, setNotifSmsOrderReceipts] = useState(false);
  const [notifEmailEnabled, setNotifEmailEnabled] = useState(false);
  const [notifEmailTemplate, setNotifEmailTemplate] = useState('');
  const [notifEmailFirstDay, setNotifEmailFirstDay] = useState('monday');
  const [notifEmailFirstTime, setNotifEmailFirstTime] = useState('08:00');
  const [notifEmailSecondDay, setNotifEmailSecondDay] = useState('wednesday');
  const [notifEmailSecondTime, setNotifEmailSecondTime] = useState('08:00');
  const [notifEmailOrderReceipts, setNotifEmailOrderReceipts] = useState(false);

  // Market report state
  const [marketReportEnabled, setMarketReportEnabled] = useState(false);
  const [marketReportTitle, setMarketReportTitle] = useState('Market Report');
  const [marketReportBody, setMarketReportBody] = useState('');

  // Order alert subscriptions state
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [editingAlert, setEditingAlert] = useState<any | null>(null);
  const [alertName, setAlertName] = useState('');
  const [alertPhone, setAlertPhone] = useState('');
  const [alertEmail, setAlertEmail] = useState('');
  // The days alerts go out (bitmask, bit 0 = Sun); every day = EVERY_DAY, stored as null.
  const [alertDays, setAlertDays] = useState(EVERY_DAY);
  const [alertOnNew, setAlertOnNew] = useState(true);
  const [alertOnUpdate, setAlertOnUpdate] = useState(true);
  const [alertOnCancel, setAlertOnCancel] = useState(true);
  // Active hours: the hours alerts ARE sent (the data always meant this; it was labelled "quiet hours" until 2026-10-16).
  const [alertUseActiveHours, setAlertUseActiveHours] = useState(false);
  const [alertStartTime, setAlertStartTime] = useState('06:00');
  const [alertEndTime, setAlertEndTime] = useState('20:00');

  React.useEffect(() => {
    if (pickupFollowsDeliverySchedule && deliveryEnabled) {
      setPickupAvailableDays(orderWindowCustomDays);
      setPickupPrepDays(deliveryLeadDays);
      setPickupCutoffHour(orderWindowCutoffHour);
      setPickupCutoffMinute(orderWindowCutoffMinute);
      setPickupBlackoutDates([...deliveryBlackoutDates]);
      setPickupMaxLeadDays(deliveryMaxLeadDays);
    }
  }, [pickupFollowsDeliverySchedule, deliveryEnabled, orderWindowCustomDays, deliveryLeadDays, orderWindowCutoffHour, orderWindowCutoffMinute, deliveryBlackoutDates, deliveryMaxLeadDays]);

  React.useEffect(() => {
    if (orderWindowFrequency === 'custom' && orderWindowCustomDays > 0) {
      const activeDays: number[] = [];
      for (let i = 0; i < 7; i++) {
        if (orderWindowCustomDays & (1 << i)) activeDays.push(i);
      }
      if (activeDays.length > 1) {
        let minGap = 7;
        for (let i = 0; i < activeDays.length; i++) {
          const next = (i + 1) % activeDays.length;
          const gap = (activeDays[next] - activeDays[i] + 7) % 7;
          if (gap > 0 && gap < minGap) minGap = gap;
        }
        const maxCutoff = Math.max(minGap - 1, 1);
        if (deliveryLeadDays > maxCutoff) {
          setDeliveryLeadDays(maxCutoff);
        }
      }
    }
  }, [orderWindowFrequency, orderWindowCustomDays, deliveryLeadDays]);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch products
  const { data: products = [], isLoading: productsLoading } = useQuery<Product[]>({
    queryKey: ['/api/products'],
  });

  // Fetch categories
  const { data: categories = [], isLoading: categoriesLoading } = useQuery<Category[]>({
    queryKey: ['/api/categories'],
  });

  // Customer-type price columns beside the base price (none without customer types)
  const typeGrid = useTypePriceGrid();

  // "Hide inactive items": a display filter over the list above — nothing is refetched,
  // and `products` stays whole for the drag handlers, the product form and the sizes
  // section. A row with a customer-type price typed but not saved stays on screen, so
  // the save bar never counts (or asks the admin to fix) a cell they cannot see.
  const inactiveCount = products.filter((p: Product) => !p.isActive).length;
  const hiddenProductIds = hideInactive
    ? hiddenInactiveIds(products, (p) => switchedInPlace.has(p.id) || typeGrid.columns.some((column) => typeGrid.isStaged(column, p.id)))
    : new Set<number>();

  // Fetch accounts
  const { data: accounts = [], isLoading: accountsLoading } = useQuery<any[]>({
    queryKey: ['/api/accounts'],
  });

  // Fetch users
  const { data: users = [], isLoading: usersLoading } = useQuery<User[]>({
    queryKey: ['/api/users'],
  });

  // Fetch order window config
  const { data: orderWindowConfig, isLoading: orderWindowConfigLoading } = useQuery<OrderWindowConfig | null>({
    queryKey: ['/api/order-window-config'],
  });

  const { data: notificationDefaults, isLoading: notificationDefaultsLoading } = useQuery<any>({
    queryKey: ['/api/notification-defaults'],
  });

  const { data: marketReport, isLoading: marketReportLoading } = useQuery<any>({
    queryKey: ['/api/market-report/admin'],
  });

  const { data: alertSubscriptions = [], isLoading: alertsLoading } = useQuery<any[]>({
    queryKey: ['/api/order-alerts'],
  });

  const createAlertMutation = useMutation({
    mutationFn: (data: any) => apiRequest('POST', '/api/order-alerts', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/order-alerts'], refetchType: 'active' });
      setAlertDialogOpen(false);
      toast({ title: "Subscriber added" });
    },
    onError: () => toast({ title: "Failed to add subscriber", variant: "destructive" }),
  });

  const updateAlertMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => apiRequest('PATCH', `/api/order-alerts/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/order-alerts'], refetchType: 'active' });
      setAlertDialogOpen(false);
      toast({ title: "Subscriber updated" });
    },
    onError: () => toast({ title: "Failed to update subscriber", variant: "destructive" }),
  });

  const deleteAlertMutation = useMutation({
    mutationFn: (id: number) => apiRequest('DELETE', `/api/order-alerts/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/order-alerts'], refetchType: 'active' });
      toast({ title: "Subscriber removed" });
    },
    onError: () => toast({ title: "Failed to remove subscriber", variant: "destructive" }),
  });

  const testAlertMutation = useMutation({
    mutationFn: (id: number) => apiRequest('POST', `/api/order-alerts/${id}/test`),
    onSuccess: () => toast({ title: "Test alert sent" }),
    onError: () => toast({ title: "Failed to send test alert", variant: "destructive" }),
  });

  function openAddAlert() {
    setEditingAlert(null);
    setAlertName('');
    setAlertPhone('');
    setAlertEmail('');
    setAlertOnNew(true);
    setAlertOnUpdate(true);
    setAlertOnCancel(true);
    setAlertUseActiveHours(false);
    setAlertStartTime('06:00');
    setAlertEndTime('20:00');
    setAlertDays(EVERY_DAY);
    setAlertDialogOpen(true);
  }

  function openEditAlert(sub: any) {
    setEditingAlert(sub);
    setAlertName(sub.name);
    setAlertPhone(sub.phone || '');
    setAlertEmail(sub.email || '');
    setAlertOnNew(sub.alertOnNewOrder);
    setAlertOnUpdate(sub.alertOnOrderUpdate);
    setAlertOnCancel(sub.alertOnOrderCancel);
    setAlertUseActiveHours(!!(sub.activeStartTime && sub.activeEndTime));
    setAlertStartTime(sub.activeStartTime ? shortTime(sub.activeStartTime) : '06:00');
    setAlertEndTime(sub.activeEndTime ? shortTime(sub.activeEndTime) : '20:00');
    setAlertDays(sub.activeDays ?? EVERY_DAY);
    setAlertDialogOpen(true);
  }

  const alertPhoneDigits = alertPhone.replace(/\D/g, '');
  const alertEmailTrimmed = alertEmail.trim();
  // A subscriber needs somewhere to be reached, and at least one day.
  const alertCanSave = !!alertName.trim() && (!!alertPhoneDigits || !!alertEmailTrimmed) && alertDays !== 0;

  function submitAlert() {
    const payload = {
      name: alertName.trim(),
      phone: alertPhoneDigits || null,
      email: alertEmailTrimmed || null,
      alertOnNewOrder: alertOnNew,
      alertOnOrderUpdate: alertOnUpdate,
      alertOnOrderCancel: alertOnCancel,
      activeStartTime: alertUseActiveHours ? alertStartTime : null,
      activeEndTime: alertUseActiveHours ? alertEndTime : null,
      activeDays: alertDays === EVERY_DAY ? null : alertDays,
    };
    if (editingAlert) {
      updateAlertMutation.mutate({ id: editingAlert.id, data: payload });
    } else {
      createAlertMutation.mutate(payload);
    }
  }

  function formatAlertPhone(phone: string): string {
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
    return phone;
  }

  // QuickBooks auth status
  const { data: qbAuthStatus } = useQuery<{ isAuthenticated: boolean; hasValidToken: boolean }>({
    queryKey: ["/api/quickbooks/auth-status"],
    refetchInterval: false,
  });
  const qbConnected = qbAuthStatus?.isAuthenticated && qbAuthStatus?.hasValidToken;

  // QuickBooks sync/products — load when product dialog is open and QB is connected
  const { data: qbProductSyncData, isLoading: qbProductsLoading } = useQuery<{
    qbItems: Array<{ id: string; name: string; type: string; unitPrice?: number; description?: string; isActive: boolean }>;
    freshOrderProducts: Array<{ id: number; name: string; quickbooksItemId: string | null }>;
  }>({
    queryKey: ["/api/quickbooks/sync/products"],
    enabled: !!qbConnected && productDialogOpen,
    staleTime: 30000,
  });

  // Pre-populate QB product search with product name when dialog opens
  useEffect(() => {
    if (qbProductLinkOpen && editingProduct) {
      setQbProductSearchQuery(editingProduct.name);
    } else if (!qbProductLinkOpen) {
      setQbProductSearchQuery("");
    }
  }, [qbProductLinkOpen, editingProduct]);

  // Sync order window state when config loads
  React.useEffect(() => {
    if (orderWindowConfig) {
      setOrderWindowEnabled(orderWindowConfig.enabled);
      setOrderWindowFrequency(orderWindowConfig.frequency);
      setOrderWindowCutoffHour(orderWindowConfig.cutoffHour);
      setOrderWindowCutoffMinute(orderWindowConfig.cutoffMinute);
      setOrderWindowCustomDays(orderWindowConfig.customDays);
      if (orderWindowConfig.anchorStart) {
        setOrderWindowAnchorDate(new Date(orderWindowConfig.anchorStart));
      }
      setPickupSchedulingEnabled(orderWindowConfig.pickupSchedulingEnabled ?? false);
      setPickupAvailableDays((orderWindowConfig as any).pickupAvailableDays ?? 62);
      setPickupBlackoutDates((orderWindowConfig as any).pickupBlackoutDates ?? []);
      setPickupMaxLeadDays((orderWindowConfig as any).pickupMaxLeadDays ?? 7);
      setDeliveryLeadDays((orderWindowConfig as any).deliveryLeadDays ?? 1);
      setDeliveryMaxLeadDays((orderWindowConfig as any).deliveryMaxLeadDays ?? 7);
      setDeliveryBlackoutDates((orderWindowConfig as any).deliveryBlackoutDates ?? []);
      setPickupCutoffHour((orderWindowConfig as any).pickupCutoffHour ?? 17);
      setPickupCutoffMinute((orderWindowConfig as any).pickupCutoffMinute ?? 0);
      setPickupPrepDays((orderWindowConfig as any).pickupPrepDays ?? 0);
      setDeliveryEnabled(orderWindowConfig.deliveryEnabled ?? true);
      setPickupEnabled(orderWindowConfig.pickupEnabled ?? false);
      setPickupFollowsDeliverySchedule(orderWindowConfig.pickupFollowsDeliverySchedule ?? false);
      setAutoChangeNotes(orderWindowConfig.autoChangeNotes ?? true);
      setDeliveryDaysByZone(orderWindowConfig.deliveryDaysByZone ?? false);
    }
  }, [orderWindowConfig]);

  // Sync notification defaults state when data loads
  React.useEffect(() => {
    if (notificationDefaults) {
      setNotifSmsEnabled(notificationDefaults.smsEnabled ?? false);
      setNotifSmsTemplate(notificationDefaults.smsTemplate ?? '');
      setNotifSmsFirstDay(notificationDefaults.smsFirstReminderDay ?? 'monday');
      setNotifSmsFirstTime(notificationDefaults.smsFirstReminderTime ?? '08:00');
      setNotifSmsSecondDay(notificationDefaults.smsSecondReminderDay ?? 'wednesday');
      setNotifSmsSecondTime(notificationDefaults.smsSecondReminderTime ?? '08:00');
      setNotifSmsOrderReceipts(notificationDefaults.smsOrderReceipts ?? false);
      setNotifEmailEnabled(notificationDefaults.emailEnabled ?? false);
      setNotifEmailTemplate(notificationDefaults.emailTemplate ?? '');
      setNotifEmailFirstDay(notificationDefaults.emailFirstReminderDay ?? 'monday');
      setNotifEmailFirstTime(notificationDefaults.emailFirstReminderTime ?? '08:00');
      setNotifEmailSecondDay(notificationDefaults.emailSecondReminderDay ?? 'wednesday');
      setNotifEmailSecondTime(notificationDefaults.emailSecondReminderTime ?? '08:00');
      setNotifEmailOrderReceipts(notificationDefaults.emailOrderReceipts ?? false);
    }
  }, [notificationDefaults]);

  // Sync market report state when data loads
  React.useEffect(() => {
    if (marketReport) {
      setMarketReportEnabled(marketReport.enabled ?? false);
      setMarketReportTitle(marketReport.title ?? 'Market Report');
      setMarketReportBody(marketReport.body ?? '');
    }
  }, [marketReport]);

  // Forms
  const productForm = useForm<ProductForm>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      itemName: "",
      pack: "",
      categoryId: 0,
      isActive: true,
      imagePath: "",
      subheading: "",
      unit: "",
      aliases: "",
      price: ""
    }
  });

  const categoryForm = useForm<CategoryForm>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      name: "",
      icon: "",
      isVisibleToCustomers: true
    }
  });

  const accountForm = useForm<AccountForm>({
    resolver: zodResolver(accountSchema),
    defaultValues: {
      name: "",
      phone: "",
      customUrl: "",
      isActive: true,
      customMenu: {
        categories: {},
        products: {}
      }
    }
  });

  // One form serves create and edit. Edit hides the password field, so it must
  // validate against userEditSchema — with userSchema the blank password fails
  // min(6) and Save silently does nothing (e.g. changing a user admin → staff).
  // A ref, because the resolver is captured when the form is created.
  const editingUserRef = useRef(editingUser);
  editingUserRef.current = editingUser;
  const userForm = useForm<UserForm>({
    resolver: (values, context, options) =>
      editingUserRef.current
        ? (zodResolver(userEditSchema) as unknown as Resolver<UserForm>)(values, context, options)
        : zodResolver(userSchema)(values, context, options),
    defaultValues: {
      username: "",
      email: "",
      password: "",
      role: "staff",
      isActive: true
    }
  });

  const passwordResetForm = useForm<PasswordResetForm>({
    resolver: zodResolver(passwordResetSchema),
    defaultValues: {
      newPassword: ""
    }
  });


  // Helper function to upload image to a product by ID
  const uploadImageToProduct = async (productId: number, file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    
    const response = await fetch(`${apiBasePath}/products/${productId}/upload-image`, {
      method: 'POST',
      body: formData,
    });
    
    if (!response.ok) {
      throw new Error('Upload failed');
    }
    
    return response.json();
  };

  // QuickBooks product link / unlink mutations
  const linkQBProductMutation = useMutation({
    mutationFn: ({ productId, quickbooksItemId }: { productId: number; quickbooksItemId: string }) =>
      apiRequest("POST", "/api/quickbooks/sync/link-product", { productId, quickbooksItemId }),
    onSuccess: (_data, variables) => {
      toast({ title: "Linked", description: "Product linked to QuickBooks item" });
      queryClient.invalidateQueries({ queryKey: ["/api/products"] });
      queryClient.invalidateQueries({ queryKey: ["/api/quickbooks/sync/products"] });
      setQbProductLinkOpen(false);
      // Update editingProduct in place so the dialog immediately shows the link
      setEditingProduct(prev => prev ? { ...prev, quickbooksItemId: variables.quickbooksItemId } : prev);
    },
    onError: (error) => {
      toast({ title: "Error", description: apiErrorMessage(error, "Failed to link product"), variant: "destructive" });
    },
  });

  const unlinkQBProductMutation = useMutation({
    mutationFn: ({ productId }: { productId: number }) =>
      apiRequest("POST", "/api/quickbooks/sync/unlink-product", { productId }),
    onSuccess: () => {
      toast({ title: "Unlinked", description: "Product unlinked from QuickBooks" });
      queryClient.invalidateQueries({ queryKey: ["/api/products"] });
      queryClient.invalidateQueries({ queryKey: ["/api/quickbooks/sync/products"] });
      // Update editingProduct in place so the dialog reflects the unlink immediately
      setEditingProduct(prev => prev ? { ...prev, quickbooksItemId: null } : prev);
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to unlink product", variant: "destructive" });
    },
  });

  // A saved tick changes that type's rules: its editor, the Master Menu price columns and
  // the account Menu tabs all read them.
  const invalidateCustomerTypes = (ticks: ProductTypeTick[] | undefined) => {
    if (!ticks || ticks.length === 0) return;
    queryClient.invalidateQueries({ queryKey: [getApiPath(CUSTOMER_TYPES_PATH)] });
    for (const tick of ticks) {
      queryClient.invalidateQueries({ queryKey: [getApiPath(customerTypePath(tick.customerTypeId))] });
    }
  };

  // Product mutations
  const createProductMutation = useMutation({
    mutationFn: async ({ customerTypes, ...data }: ProductSave & { customerTypes?: ProductTypeTick[] }) => {
      // The ticks go with the product: the server writes both in one transaction, so a
      // product unticked for a type is never on that type's menu, even for a moment.
      const response = await apiRequest('POST', '/api/products', {
        ...data,
        aliases: aliasesTextToArray(data.aliases),
        ...(customerTypes && customerTypes.length > 0 ? { customerTypes } : {}),
      });
      return response.json();
    },
    onSuccess: async (product: any, variables) => {
      invalidateCustomerTypes(variables.customerTypes);
      // If there's a pending image, upload it to the newly created product
      if (pendingImageFile && product?.id) {
        setIsUploadingImage(true);
        try {
          await uploadImageToProduct(product.id, pendingImageFile);
        } catch (error) {
          console.error('Failed to upload pending image:', error);
          toast({
            title: "Note",
            description: "Product created, but image upload failed. You can try uploading again.",
            variant: "destructive"
          });
        } finally {
          setIsUploadingImage(false);
        }
      }
      
      // Clear pending image state
      setPendingImageFile(null);
      setPendingImagePreview(null);
      
      queryClient.invalidateQueries({ queryKey: ['/api/products'] });
      queryClient.invalidateQueries({ queryKey: ['/api/products/active'] });
      setProductDialogOpen(false);
      productForm.reset();
      toast({
        title: "Success",
        description: "Product created successfully"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: parseApiError(error).message ?? "Failed to create product",
        variant: "destructive"
      });
    }
  });

  const updateProductMutation = useMutation({
    mutationFn: async ({ customerTypes, ...data }: { id: number } & ProductSave & { customerTypes?: ProductTypeTick[] }) => {
      // PATCH (not the legacy PUT): same fields plus aliases + price, and the server
      // does the label-preserving alias merge there. Aliases go only when the caller
      // has them — the drag-and-drop category move has none (productPatchBody).
      const response = await apiRequest('PATCH', `/api/products/${data.id}`, productPatchBody(data));
      // Then the "Shown to <type>" ticks the admin changed, one type at a time: the
      // server reads the unit and category just saved. A tick that fails leaves the
      // product saved — say so rather than report the whole save as failed.
      let typeTickError: string | null = null;
      for (const tick of customerTypes ?? []) {
        try {
          await apiRequest('PUT', productCustomerTypePath(data.id, tick.customerTypeId), { shown: tick.shown });
        } catch (error) {
          typeTickError = parseApiError(error).message ?? "Please try again.";
        }
      }
      return { response, productId: data.id, categoryId: data.categoryId, typeTickError };
    },
    onMutate: async (newData) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['/api/products'] });
      
      // Snapshot the previous value
      const previousProducts = queryClient.getQueryData(['/api/products']);
      
      // Optimistically update the cache
      queryClient.setQueryData(['/api/products'], (old: Product[] | undefined) => {
        if (!old) return old;
        return old.map(p => 
          p.id === newData.id 
            ? { ...p, categoryId: newData.categoryId, name: newData.name, isActive: newData.isActive, price: newData.price }
            : p
        );
      });
      
      // Return context with the snapshotted value
      return { previousProducts };
    },
    onSuccess: async ({ productId, typeTickError }, variables) => {
      invalidateCustomerTypes(variables.customerTypes);
      if (typeTickError) {
        toast({
          title: "Note",
          description: `Product updated, but its customer types didn't save. ${typeTickError}`,
          variant: "destructive"
        });
      }
      // If there's a pending image, upload it after successful product update
      if (pendingImageFile && productId) {
        setIsUploadingImage(true);
        try {
          await uploadImageToProduct(pictureHolderId(productId), pendingImageFile);
        } catch (error) {
          console.error('Failed to upload pending image:', error);
          toast({
            title: "Note",
            description: "Product updated, but image upload failed. You can try uploading again.",
            variant: "destructive"
          });
        } finally {
          setIsUploadingImage(false);
        }
      }
      
      // Clear pending image state
      setPendingImageFile(null);
      setPendingImagePreview(null);
      
      // Force refetch both products and categories to ensure UI updates
      await queryClient.invalidateQueries({ queryKey: ['/api/products'] });
      await queryClient.invalidateQueries({ queryKey: ['/api/products/active'] });
      await queryClient.invalidateQueries({ queryKey: ['/api/categories'] });
      await queryClient.refetchQueries({ queryKey: ['/api/products'] });
      
      setProductDialogOpen(false);
      setEditingProduct(null);
      productForm.reset();
      toast({
        title: "Success",
        description: "Product updated successfully"
      });
    },
    onError: (error: any, newData, context) => {
      // Roll back to previous value on error
      if (context?.previousProducts) {
        queryClient.setQueryData(['/api/products'], context.previousProducts);
      }
      toast({
        title: "Error",
        description: parseApiError(error).message ?? "Failed to update product",
        variant: "destructive"
      });
    }
  });

  const handleImageUpload = async (file: File) => {
    // For both new and existing products, store as pending until Save is clicked
    // Create preview URL for immediate display
    const previewUrl = URL.createObjectURL(file);
    setPendingImageFile(file);
    setPendingImagePreview(previewUrl);
  };

  const updateProductImageMutation = useMutation({
    mutationFn: ({ productId, imageURL }: { productId: number; imageURL: string }) =>
      apiRequest('PUT', `/api/products/${productId}/image`, { imageURL }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/products'] });
      queryClient.invalidateQueries({ queryKey: ['/api/products/active'] });
      toast({
        title: "Success",
        description: "Product image updated successfully"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: "Failed to update product image",
        variant: "destructive"
      });
    }
  });

  const deleteProductMutation = useMutation({
    mutationFn: async (vars: { id: number; name: string }) => {
      const res = await apiRequest('DELETE', `/api/products/${vars.id}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/products'] });
      queryClient.invalidateQueries({ queryKey: ['/api/products/active'] });
      toast({
        title: "Success",
        description: "Product deleted successfully"
      });
    },
    onError: (error: any, variables) => {
      // apiRequest throws Error('${status}: ${rawBody}'); detect the 409
      // PRODUCT_IN_USE response and offer deactivation instead.
      const match = typeof error?.message === 'string'
        ? error.message.match(/^(\d{3}):\s*([\s\S]*)$/)
        : null;
      if (match && parseInt(match[1], 10) === 409) {
        try {
          const parsed = JSON.parse(match[2]);
          if (parsed?.code === 'PRODUCT_IN_USE') {
            setDeactivateConfirmState({
              open: true,
              productId: variables.id,
              productName: variables.name,
              itemCount: parsed.itemCount ?? 0,
            });
            return;
          }
        } catch {
          // fall through to generic toast
        }
      }
      toast({
        title: "Error",
        description: error?.message || "Failed to delete product",
        variant: "destructive"
      });
    }
  });

  const deactivateProductMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest('PATCH', `/api/products/${id}`, { isActive: false });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/products'] });
      queryClient.invalidateQueries({ queryKey: ['/api/products/active'] });
      queryClient.invalidateQueries({ queryKey: ['/api/categories'] });
      setDeactivateConfirmState({ open: false, productId: null, productName: '', itemCount: 0 });
      toast({
        title: "Product deactivated",
        description: "Hidden from customers, order history preserved."
      });
    },
    onError: (error: any) => {
      setDeactivateConfirmState({ open: false, productId: null, productName: '', itemCount: 0 });
      toast({
        title: "Error",
        description: error?.message || "Failed to deactivate product",
        variant: "destructive"
      });
    }
  });

  // Category mutations
  const createCategoryMutation = useMutation({
    mutationFn: (data: CategoryForm) => apiRequest('POST', '/api/categories', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/categories'] });
      setCategoryDialogOpen(false);
      categoryForm.reset();
      toast({
        title: "Success",
        description: "Category created successfully"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create category",
        variant: "destructive"
      });
    }
  });

  const updateCategoryMutation = useMutation({
    mutationFn: (data: { id: number } & CategoryForm) => 
      apiRequest('PUT', `/api/categories/${data.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/categories'] });
      setCategoryDialogOpen(false);
      setEditingCategory(null);
      categoryForm.reset();
      toast({
        title: "Success",
        description: "Category updated successfully"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update category",
        variant: "destructive"
      });
    }
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest('DELETE', `/api/categories/${id}`);
      if (!res.ok) {
        if (res.status === 409) {
          throw new Error('Cannot delete category: it\'s being used by products');
        }
        throw new Error('Failed to delete category');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/categories'] });
      toast({
        title: "Success",
        description: "Category deleted successfully"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete category",
        variant: "destructive"
      });
    }
  });

  // Reorder mutations
  const reorderCategoriesMutation = useMutation({
    mutationFn: (categoryIds: number[]) => 
      apiRequest('PUT', '/api/categories/reorder', { categoryIds }),
    onMutate: async (categoryIds) => {
      await queryClient.cancelQueries({ queryKey: ['/api/categories'] });
      const previousCategories = queryClient.getQueryData(['/api/categories']);
      
      // Optimistically reorder categories in cache
      queryClient.setQueryData(['/api/categories'], (old: Category[] | undefined) => {
        if (!old) return old;
        const reordered = categoryIds.map((id, index) => {
          const cat = old.find(c => c.id === id);
          return cat ? { ...cat, sortOrder: index } : null;
        }).filter(Boolean) as Category[];
        return reordered;
      });
      
      return { previousCategories };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/categories'] });
      toast({
        title: "Success",
        description: "Categories reordered successfully"
      });
    },
    onError: (error: any, categoryIds, context) => {
      if (context?.previousCategories) {
        queryClient.setQueryData(['/api/categories'], context.previousCategories);
      }
      toast({
        title: "Error",
        description: "Failed to reorder categories",
        variant: "destructive"
      });
    }
  });

  // User mutations
  const createUserMutation = useMutation({
    mutationFn: (data: UserForm) => apiRequest('POST', '/api/users', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/users'] });
      setUserDialogOpen(false);
      userForm.reset();
      toast({
        title: "Success",
        description: "User created successfully"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create user",
        variant: "destructive"
      });
    }
  });

  const updateUserMutation = useMutation({
    mutationFn: (data: { id: number } & UserEditForm) => 
      apiRequest('PUT', `/api/users/${data.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/users'] });
      setUserDialogOpen(false);
      setEditingUser(null);
      userForm.reset();
      toast({
        title: "Success",
        description: "User updated successfully"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update user",
        variant: "destructive"
      });
    }
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest('DELETE', `/api/users/${id}`);
      if (!res.ok) {
        throw new Error('Failed to delete user');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/users'] });
      toast({
        title: "Success",
        description: "User deleted successfully"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete user",
        variant: "destructive"
      });
    }
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ userId, newPassword }: { userId: number; newPassword: string }) =>
      apiRequest('PUT', `/api/users/${userId}/reset-password`, { newPassword, requestingUserRole: currentUserRole }),
    onSuccess: () => {
      setPasswordResetDialogOpen(false);
      setResetPasswordUser(null);
      passwordResetForm.reset();
      toast({
        title: "Success",
        description: "Password reset successfully"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to reset password",
        variant: "destructive"
      });
    }
  });

  // Kiosk PIN mutation
  const setKioskPinMutation = useMutation({
    mutationFn: (data: { pin: string; sessionTimeout: number }) =>
      apiRequest('POST', '/api/kiosk/set-pin', data),
    onSuccess: () => {
      setKioskPin('');
      setShowKioskPin(false);
      toast({
        title: "Success",
        description: "Kiosk PIN configured successfully"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to set kiosk PIN",
        variant: "destructive"
      });
    }
  });

  // Order window config mutation
  const updateOrderWindowMutation = useMutation({
    mutationFn: (data: {
      enabled: boolean;
      frequency: string;
      cutoffHour: number;
      cutoffMinute: number;
      customDays: number;
      anchorStart?: string;
      pickupSchedulingEnabled?: boolean;
      pickupAvailableDays?: number;
      pickupBlackoutDates?: string[];
      pickupMaxLeadDays?: number;
      deliveryLeadDays?: number;
      deliveryMaxLeadDays?: number;
      deliveryBlackoutDates?: string[];
      deliveryEnabled?: boolean;
      pickupEnabled?: boolean;
      pickupFollowsDeliverySchedule?: boolean;
      pickupCutoffHour?: number;
      pickupCutoffMinute?: number;
      pickupPrepDays?: number;
      deliveryDaysByZone?: boolean;
    }) => apiRequest('PUT', '/api/order-window-config', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/order-window-config'] });
      toast({
        title: "Success",
        description: "Fulfillment settings saved"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save order window settings",
        variant: "destructive"
      });
    }
  });

  // Notification defaults mutation
  const saveNotificationDefaultsMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiRequest('PATCH', '/api/notification-defaults', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/notification-defaults'] });
      toast({ title: "Success", description: "Notification defaults saved" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to save notification defaults", variant: "destructive" });
    }
  });

  const handleSaveNotificationDefaults = () => {
    saveNotificationDefaultsMutation.mutate({
      smsEnabled: notifSmsEnabled,
      smsTemplate: notifSmsTemplate || null,
      smsFirstReminderDay: notifSmsFirstDay,
      smsFirstReminderTime: notifSmsFirstTime,
      smsSecondReminderDay: notifSmsSecondDay,
      smsSecondReminderTime: notifSmsSecondTime,
      smsOrderReceipts: notifSmsOrderReceipts,
      emailEnabled: notifEmailEnabled,
      emailTemplate: notifEmailTemplate || null,
      emailFirstReminderDay: notifEmailFirstDay,
      emailFirstReminderTime: notifEmailFirstTime,
      emailSecondReminderDay: notifEmailSecondDay,
      emailSecondReminderTime: notifEmailSecondTime,
      emailOrderReceipts: notifEmailOrderReceipts,
    });
  };

  const saveMarketReportMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiRequest('PATCH', '/api/market-report', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/market-report/admin'] });
      queryClient.invalidateQueries({ queryKey: ['/api/market-report'] });
      toast({ title: "Success", description: "Market report saved" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to save market report", variant: "destructive" });
    }
  });

  const handleSaveMarketReport = () => {
    saveMarketReportMutation.mutate({
      enabled: marketReportEnabled,
      title: marketReportTitle.trim() || 'Market Report',
      body: marketReportBody.trim() ? marketReportBody : null,
    });
  };

  const reorderProductsMutation = useMutation({
    mutationFn: (productIds: number[]) => 
      apiRequest('PUT', '/api/products/reorder', { productIds }),
    onMutate: async (productIds) => {
      await queryClient.cancelQueries({ queryKey: ['/api/products'] });
      const previousProducts = queryClient.getQueryData(['/api/products']);
      
      // Optimistically reorder products in cache
      queryClient.setQueryData(['/api/products'], (old: Product[] | undefined) => {
        if (!old) return old;
        // Update sortOrder for the reordered products
        const reorderedIds = new Set(productIds);
        const otherProducts = old.filter(p => !reorderedIds.has(p.id));
        const reordered = productIds.map((id, index) => {
          const prod = old.find(p => p.id === id);
          return prod ? { ...prod, sortOrder: index } : null;
        }).filter(Boolean) as Product[];
        return [...reordered, ...otherProducts];
      });
      
      return { previousProducts };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/products'] });
      toast({
        title: "Success",
        description: "Products reordered successfully"
      });
    },
    onError: (error: any, productIds, context) => {
      if (context?.previousProducts) {
        queryClient.setQueryData(['/api/products'], context.previousProducts);
      }
      toast({
        title: "Error",
        description: "Failed to reorder products",
        variant: "destructive"
      });
    }
  });

  // Handlers
  const handleProductSubmit = (data: ProductForm) => {
    // The ticks that change something, read against the unit and category being saved.
    const customerTypes = pendingTypeTicks(
      { id: editingProduct?.id ?? 0, categoryId: data.categoryId, unit: data.unit },
      productTypeChoices,
      (typeId) => queryClient.getQueryData<CustomerTypeDetail>([getApiPath(customerTypePath(typeId))]),
    );
    // The name the parts make, for the list while the save is in flight; the server makes its own.
    const name = productFormName(data);
    if (editingProduct) {
      const updateData = { id: editingProduct.id, ...data, name, customerTypes };
      updateProductMutation.mutate(updateData);
    } else {
      createProductMutation.mutate({ ...data, name, customerTypes });
    }
  };

  const handleCategorySubmit = (data: CategoryForm) => {
    if (editingCategory) {
      updateCategoryMutation.mutate({ id: editingCategory.id, ...data });
    } else {
      createCategoryMutation.mutate(data);
    }
  };

  // The official name the item form's item and unit make (shared/productNames.ts): the
  // stored name while neither changed, else the two joined (an old row's pack between).
  const productFormName = (values: { itemName?: string; pack?: string; unit?: string }) =>
    nextProductName(editingProduct, {
      item: tidyNamePart(values.itemName ?? ""),
      pack: tidyNamePart(values.pack ?? ""),
      unit: normalizeProductUnit(values.unit ?? "") || null,
    });

  // An item has one picture. Its units borrow it from the unit they are linked to, so a
  // new picture goes there — unless the unit open in the form has one of its own.
  const pictureHolderId = (id: number): number => {
    const unit = products.find((p: Product) => p.id === id);
    if (!unit || unit.sizeOfProductId == null) return id;
    const lead = products.find((p: Product) => p.id === unit.sizeOfProductId);
    return lead && (!unit.imagePath || unit.imagePath === lead.imagePath) ? lead.id : id;
  };

  // The item form's picture (product-name-preview.tsx): the unit open in the form as
  // typed, and the item's other units as the save will leave them — a new item name
  // reaches every one of them (server: services/productSizes.ts followItem).
  const productPreviewMembers = (): PreviewMember[] => {
    const values = productForm.watch();
    const typedItem = tidyNamePart(values.itemName ?? "");
    const typed: PreviewMember = {
      id: editingProduct?.id ?? "new",
      name: productFormName(values),
      item: typedItem,
      pack: tidyNamePart(values.pack ?? ""),
      unit: normalizeProductUnit(values.unit ?? "") || null,
      price: values.price?.trim() || null,
      subheading: values.subheading?.trim() || null,
    };
    if (!editingProduct) return [typed];
    const units = unitsOfItem(editingProduct, products).filter((p) => p.isActive || p.id === editingProduct.id);
    if (units.length < 2) return [typed];
    const itemMoved = typedItem !== productParts(editingProduct).item;
    return units.map((p) => {
      if (p.id === editingProduct.id) return typed;
      const stored = productParts(p);
      const item = itemMoved ? typedItem : stored.item;
      const pack = itemMoved && p.itemName == null ? "" : stored.pack;
      return {
        id: p.id,
        name: itemMoved ? nextProductName(p, { item, pack, unit: p.unit ?? null }) : p.name,
        item,
        pack,
        unit: p.unit,
        price: p.price,
        subheading: p.subheading,
      };
    });
  };

  // The item form's unit chips open the same form on another unit of the item. Unsaved
  // changes to the unit open now would be lost, so ask first.
  const openUnit = (unit: Product) => {
    const dirty = productForm.formState.isDirty || !!pendingImageFile || Object.keys(productTypeChoices).length > 0;
    if (dirty && !confirm(`Discard your unsaved changes to the ${editingProduct?.unit ?? "open"} unit?`)) return;
    handleEditProduct(products.find((p: Product) => p.id === unit.id) ?? unit);
  };

  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    productForm.reset({
      itemName: productParts(product).item,
      pack: productParts(product).pack,
      categoryId: product.categoryId,
      isActive: product.isActive,
      imagePath: product.imagePath || "",
      subheading: product.subheading || "",
      unit: product.unit || "",
      aliases: activeAliases(product.aliases).join(", "),
      price: product.price || "",
      taxPercent: product.taxPercent || ""
    });
    setPendingImageFile(null);
    setPendingImagePreview(null);
    setProductDialogOpen(true);
  };

  const handleDeleteProduct = (id: number) => {
    if (confirm('Are you sure you want to delete this product?')) {
      const product = products.find((p: Product) => p.id === id);
      deleteProductMutation.mutate({ id, name: product?.name ?? 'this product' });
    }
  };
  
  const updateProductPriceMutation = useMutation({
    mutationFn: async (data: { id: number; price: string }) => {
      return apiRequest('PATCH', `/api/products/${data.id}`, { price: data.price });
    },
    onMutate: async ({ id, price }) => {
      await queryClient.cancelQueries({ queryKey: ['/api/products'] });
      const previous = queryClient.getQueryData(['/api/products']);
      queryClient.setQueryData(['/api/products'], (old: Product[] | undefined) => {
        if (!old) return old;
        return old.map(p => p.id === id ? { ...p, price } : p);
      });
      return { previous };
    },
    onError: (error: any, _vars: any, context: any) => {
      if (context?.previous) queryClient.setQueryData(['/api/products'], context.previous);
      toast({
        title: "Error",
        description: error.message || "Failed to update price",
        variant: "destructive"
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/products'] });
    },
  });
  
  // The row's Active / Inactive badge: saves `isActive` alone, the list switching at once.
  const updateProductActiveMutation = useMutation({
    mutationFn: async (data: { id: number; isActive: boolean }) => {
      const { path, body } = productActiveRequest(data.id, data.isActive);
      return apiRequest('PATCH', path, body);
    },
    onMutate: async ({ id, isActive }) => {
      await queryClient.cancelQueries({ queryKey: ['/api/products'] });
      const previous = queryClient.getQueryData(['/api/products']);
      queryClient.setQueryData(['/api/products'], (old: Product[] | undefined) => withProductActive(old, id, isActive));
      return { previous };
    },
    onError: (error: any, _vars: any, context: any) => {
      if (context?.previous) queryClient.setQueryData(['/api/products'], context.previous);
      toast({
        title: "Error",
        description: error.message || "Failed to update status",
        variant: "destructive"
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/products'] });
    },
  });

  const handleActiveChange = (id: number, isActive: boolean) => {
    setSwitchedInPlace((current) => new Set(current).add(id));
    updateProductActiveMutation.mutate({ id, isActive });
  };

  const handlePriceChange = (id: number, price: string) => {
    // Validate and sanitize price input - only allow numeric values
    const sanitized = price.replace(/[^0-9.]/g, '');
    // Only allow one decimal point
    const parts = sanitized.split('.');
    const validPrice = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : sanitized;
    updateProductPriceMutation.mutate({ id, price: validPrice });
  };

  const handleEditCategory = (category: Category) => {
    setEditingCategory(category);
    categoryForm.reset({
      name: category.name,
      icon: category.icon,
      isVisibleToCustomers: category.isVisibleToCustomers !== false
    });
    setCategoryDialogOpen(true);
  };

  const handleDeleteCategory = (id: number) => {
    if (confirm('Are you sure you want to delete this category?')) {
      deleteCategoryMutation.mutate(id);
    }
  };

  // User handlers
  const handleUserSubmit = (data: UserForm) => {
    if (editingUser) {
      // For editing, we don't need password if not provided
      const updateData = {
        id: editingUser.id,
        username: data.username,
        email: data.email,
        role: data.role,
        isActive: data.isActive
      };
      updateUserMutation.mutate(updateData);
    } else {
      createUserMutation.mutate(data);
    }
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    userForm.reset({
      username: user.username,
      email: user.email || "",
      password: "", // Don't pre-fill password
      role: user.role as "admin" | "staff",
      isActive: user.isActive
    });
    setUserDialogOpen(true);
  };

  const handleDeleteUser = (id: number) => {
    if (confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      deleteUserMutation.mutate(id);
    }
  };

  const handleResetPassword = (user: User) => {
    setResetPasswordUser(user);
    passwordResetForm.reset({ newPassword: "" });
    setPasswordResetDialogOpen(true);
  };

  const handlePasswordResetSubmit = (data: PasswordResetForm) => {
    if (resetPasswordUser) {
      resetPasswordMutation.mutate({
        userId: resetPasswordUser.id,
        newPassword: data.newPassword
      });
    }
  };




  // Toggle category function
  const toggleCategory = (categoryId: number) => {
    setOpenCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Drag and drop handlers
  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(event.active.id.toString());
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragId(null);
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const activeId = active.id.toString();
    const overId = over.id.toString();

    // Check if we're dragging categories
    if (activeId.startsWith('category-') && overId.startsWith('category-')) {
      console.log('=== FRONTEND CATEGORY REORDER ===');
      console.log('activeId:', activeId, 'overId:', overId);
      
      const activeCategoryId = parseInt(activeId.replace('category-', ''));
      const overCategoryId = parseInt(overId.replace('category-', ''));
      
      console.log('activeCategoryId:', activeCategoryId, 'overCategoryId:', overCategoryId);
      
      const categoryIds = categories.map((c: Category) => c.id);
      const oldIndex = categoryIds.indexOf(activeCategoryId);
      const newIndex = categoryIds.indexOf(overCategoryId);
      
      console.log('categoryIds:', categoryIds);
      console.log('oldIndex:', oldIndex, 'newIndex:', newIndex);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = [...categoryIds];
        const [removed] = newOrder.splice(oldIndex, 1);
        newOrder.splice(newIndex, 0, removed);
        console.log('newOrder:', newOrder);
        reorderCategoriesMutation.mutate(newOrder);
      }
    }
    // Check if we're dragging a product onto a category (cross-category move)
    else if (activeId.startsWith('product-') && overId.startsWith('category-')) {
      const activeProductId = parseInt(activeId.replace('product-', ''));
      const targetCategoryId = parseInt(overId.replace('category-', ''));
      
      const activeProduct = products.find((p: Product) => p.id === activeProductId);
      
      if (activeProduct && activeProduct.categoryId !== targetCategoryId) {
        console.log('=== CROSS-CATEGORY MOVE (to category) ===');
        console.log('Moving product', activeProductId, 'to category', targetCategoryId);
        
        updateProductMutation.mutate({
          id: activeProductId,
          name: activeProduct.name,
          categoryId: targetCategoryId,
          isActive: activeProduct.isActive,
          imagePath: activeProduct.imagePath || undefined,
          subheading: activeProduct.subheading || undefined
        });
      }
    }
    // Check if we're dragging products
    else if (activeId.startsWith('product-') && overId.startsWith('product-')) {
      const activeProductId = parseInt(activeId.replace('product-', ''));
      const overProductId = parseInt(overId.replace('product-', ''));
      
      const activeProduct = products.find((p: Product) => p.id === activeProductId);
      const overProduct = products.find((p: Product) => p.id === overProductId);
      
      if (activeProduct && overProduct) {
        // Cross-category move: product dropped on product in different category
        if (activeProduct.categoryId !== overProduct.categoryId) {
          console.log('=== CROSS-CATEGORY MOVE (to product) ===');
          console.log('Moving product', activeProductId, 'to category', overProduct.categoryId);
          
          updateProductMutation.mutate({
            id: activeProductId,
            name: activeProduct.name,
            categoryId: overProduct.categoryId,
            isActive: activeProduct.isActive,
            imagePath: activeProduct.imagePath || undefined,
            subheading: activeProduct.subheading || undefined
          });
        }
        // Same category reorder
        else {
          // The whole category, rows hidden by "Hide inactive items" included — the
          // reorder renumbers exactly the ids it is sent, and a hidden row moves with
          // the row above it (lib/masterMenuFilter.ts). An item's units move with it
          // (lib/itemUnits.ts).
          const { ids: categoryProducts, riders } = categoryReorderList(
            products.filter((p: Product) => p.categoryId === activeProduct.categoryId),
            hiddenProductIds,
          );

          const newOrder = reorderCategoryIds(categoryProducts, activeProductId, overProductId, riders);

          if (newOrder) {
            console.log('=== FRONTEND REORDER ===');
            console.log('categoryProducts:', categoryProducts);
            console.log('newOrder:', newOrder);
            console.log('activeProductId:', activeProductId, 'overProductId:', overProductId);
            
            reorderProductsMutation.mutate(newOrder);
          }
        }
      }
    }
  };

  // Get product image function (potatoes get specific images, others get org logo or FreshOrder logo)
  const getProductImage = (productName: string, product?: any): string => {
    // First check if the product has a custom uploaded image
    if (product?.imagePath) {
      return product.imagePath;
    }
    
    // Fallback: use org favicon if org has one (square dimensions), otherwise use FreshOrder logo
    const fallbackLogo = organization?.faviconUrl || defaultProductLogo;
    
    // Check if this is a potato product
    const lowerName = productName.toLowerCase();
    const isPotatoProduct = lowerName.includes('potato') || lowerName.includes('yam') || lowerName.includes('fingerling') || productName === 'Trio' || productName === '3L Potato';
    
    if (!isPotatoProduct) {
      return fallbackLogo;
    }
    
    // Exact product name matches for uploaded potato images only
    switch (productName) {
      case "90ct Baking Potato":
        return potato90ctBaking;
      case "70ct Baking Potato":
        return potato70ctBaking;
      case "50lb Fingerling":
        return potato50lbFingerling;
      case "25lb Fingerling":
        return potato25lbFingerling;
      case "Purple Yam":
        return purpleYam;
      case "Purple yam":
        return purpleYam;
      case "10lb Red Potato":
        return potato10lbRed;
      case "10lb White Potato":
        return potato10lbWhite;
      case "10lb Yukon Potato":
        return potato10lbYukon;
      case "10lb Russet Potato":
        return potato10lbRusset;
      case "50lb Red Potato":
        return potato50lbRed;
      case "50lb Yukon Potato":
        return potato50lbYukon;
      case "50lb White Potato":
        return potato50lbWhite;
      case "A Red Potato":
        return potatoARed;
      case "B Red Potato":
        return potatoBRed;
      case "C Red Potato":
        return potatoCRed;
      case "C Yellow Potato":
        return potatoCYellow;
      case "B Yellow Potato":
        return potatoBYellow;
      case "A Yellow Potato":
        return potatoAYellow;
      case "Sweet Potato":
        return potatoSweetPotato;
      case "Parisian Potato":
        return potatoParisian;
      case "C White Potato":
        return potatoCWhite;
      case "B White Potato":
        return potatoBWhite;
      case "A White Potato":
        return potatoAWhite;
      case "3L Potato":
        return potato3L;
      case "Trio":
        return potatoTrio;
    }

    const lowerProductName = productName.toLowerCase();
    
    // Sweet potatoes
    if (lowerProductName.includes('sweet')) {
      return potatoSweet;
    }
    
    // Red potatoes
    if (lowerProductName.includes('red')) {
      return potatoVariety;
    }
    
    // Yellow/Yukon potatoes
    if (lowerProductName.includes('yellow') || lowerProductName.includes('yukon')) {
      return potatoYellow;
    }
    
    // Small potatoes (3L, 2L, 1L, fingerling)
    if (lowerProductName.includes('3l') || lowerProductName.includes('2l') || lowerProductName.includes('1l') || lowerProductName.includes('fingerling')) {
      return potatoSmall;
    }
    
    // Default to russet/white for all other potatoes
    return potatoRusset;
  };

  return (
    <div>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-0">
        {/* Mobile: Dropdown selector */}
        <div className="md:hidden max-w-7xl mx-auto px-1 relative z-10 mb-2 mt-8">
          <select
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
            className="w-full h-10 px-3 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-cn-brand-blue"
          >
            <option value="catalog">Menu</option>
            <option value="delivery">Fulfillment</option>
            <option value="admin">Admin</option>
            <option value="history">History</option>
            {kioskEnabled && <option value="kiosk">Kiosk Mode</option>}
            <option value="notifications">Notifications</option>
          </select>
        </div>

        {/* Desktop: Original tab bar. Six tabs at px-8 are ~800px wide, so between
            md and lg they get tighter padding — otherwise the strip widens the page. */}
        <div className="hidden md:block max-w-7xl mx-auto px-3 sm:px-6 mt-8">
          <TabsList className="h-12 p-0 bg-[#C4DAFB] rounded-t-lg rounded-b-none border-0 relative top-2 z-0 ml-1 sm:ml-2 max-lg:[&>button]:px-5">
            <TabsTrigger
              value="catalog"
              className="h-full px-2 sm:px-8 rounded-t-lg rounded-b-none data-[state=active]:bg-white data-[state=active]:text-google-blue data-[state=inactive]:bg-transparent data-[state=inactive]:text-gray-700 border-0 data-[state=active]:z-10 text-xs sm:text-sm"
            >
              <span className="hidden sm:inline">Master Menu</span>
              <span className="sm:hidden">Menu</span>
            </TabsTrigger>
            <TabsTrigger
              value="delivery"
              className="h-full px-2 sm:px-8 rounded-t-lg rounded-b-none data-[state=active]:bg-white data-[state=active]:text-google-blue data-[state=inactive]:bg-transparent data-[state=inactive]:text-gray-700 border-0 data-[state=active]:z-10 text-xs sm:text-sm"
            >
              Fulfillment
            </TabsTrigger>
            <TabsTrigger
              value="admin"
              className="h-full px-2 sm:px-8 rounded-t-lg rounded-b-none data-[state=active]:bg-white data-[state=active]:text-google-blue data-[state=inactive]:bg-transparent data-[state=inactive]:text-gray-700 border-0 data-[state=active]:z-10 text-xs sm:text-sm"
            >
              Admin
            </TabsTrigger>
            <TabsTrigger
              value="history"
              className="h-full px-2 sm:px-8 rounded-t-lg rounded-b-none data-[state=active]:bg-white data-[state=active]:text-google-blue data-[state=inactive]:bg-transparent data-[state=inactive]:text-gray-700 border-0 data-[state=active]:z-10 text-xs sm:text-sm"
            >
              History
            </TabsTrigger>
            {kioskEnabled && (
              <TabsTrigger
                value="kiosk"
                className="h-full px-2 sm:px-8 rounded-t-lg rounded-b-none data-[state=active]:bg-white data-[state=active]:text-google-blue data-[state=inactive]:bg-transparent data-[state=inactive]:text-gray-700 border-0 data-[state=active]:z-10 text-xs sm:text-sm"
              >
                Kiosk Mode
              </TabsTrigger>
            )}
            <TabsTrigger
              value="notifications"
              className="h-full px-2 sm:px-8 rounded-t-lg rounded-b-none data-[state=active]:bg-white data-[state=active]:text-google-blue data-[state=inactive]:bg-transparent data-[state=inactive]:text-gray-700 border-0 data-[state=active]:z-10 text-xs sm:text-sm"
            >
              Notifications
            </TabsTrigger>
          </TabsList>
        </div>
        
        <div className="max-w-7xl mx-auto px-0 sm:px-3 md:px-6 pb-3 sm:pb-6 relative z-20">


        <TabsContent value="catalog" className="space-y-4">
          <Card className="settings-card rounded-none md:rounded-lg border-x-0 md:border mx-0 p-0">
            <CardHeader className="border-b border-gray-100 bg-gray-50/50 px-2 md:px-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-start gap-4">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex items-center gap-2">
                    <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      size="icon"
                      className="bg-cn-brand-green hover:bg-[hsl(140,27%,88%)] hover:text-cn-brand-green" 
                      data-testid="button-add-new"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem
                      onClick={() => {
                        setCreateType('product');
                        setEditingProduct(null);
                        productForm.reset({
                          itemName: "",
                          pack: "",
                          categoryId: 0,
                          isActive: true,
                          imagePath: "",
                          subheading: "",
                          unit: ""
                        });
                        setPendingImageFile(null);
                        setPendingImagePreview(null);
                        setProductDialogOpen(true);
                      }}
                      data-testid="menu-add-product"
                    >
                      Add Item
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        setCreateType('category');
                        setEditingCategory(null);
                        categoryForm.reset({
                          name: "",
                          icon: "",
                          isVisibleToCustomers: true
                        });
                        setCategoryDialogOpen(true);
                      }}
                      data-testid="menu-add-category"
                    >
                      Add Category
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                    <label className="ml-2 flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={hideInactive}
                        onCheckedChange={(checked) => {
                          setHideInactive(!!checked);
                          writeHideInactive(!!checked);
                          setSwitchedInPlace(new Set());
                        }}
                        data-testid="checkbox-hide-inactive"
                      />
                      <span className="text-sm">
                        Hide inactive items
                        {/* The count is there so a shorter menu never reads as deleted products. */}
                        {hideInactive && hiddenProductIds.size > 0 && (
                          <span className="text-gray-500" data-testid="text-hidden-inactive-count"> ({hiddenProductIds.size} hidden)</span>
                        )}
                        {!hideInactive && inactiveCount > 0 && (
                          <span className="text-gray-500" data-testid="text-hidden-inactive-count"> ({inactiveCount} inactive)</span>
                        )}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0 md:p-6">
              {productsLoading || categoriesLoading ? (
                <div className="text-center py-8">Loading catalog...</div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                >
                  <div className="space-y-0 md:space-y-4">
                    <SortableContext
                      items={categories.map((c: Category) => `category-${c.id}`)}
                      strategy={verticalListSortingStrategy}
                    >
                      {categories.map((category: Category) => {
                        const categoryProducts = products.filter((product: Product) => product.categoryId === category.id);
                        const drawn = categoryProducts.filter((product: Product) => !hiddenProductIds.has(product.id));
                        return (
                        <SortableCategory
                          key={category.id}
                          category={category}
                          products={drawn}
                          hiddenCount={categoryProducts.length - drawn.length}
                          isOpen={openCategories.has(category.id)}
                          onToggle={() => toggleCategory(category.id)}
                          onEditCategory={handleEditCategory}
                          onDeleteCategory={handleDeleteCategory}
                          onEditProduct={handleEditProduct}
                          onDeleteProduct={handleDeleteProduct}
                          onPriceChange={handlePriceChange}
                          onActiveChange={handleActiveChange}
                          getProductImage={getProductImage}
                          typeGrid={typeGrid}
                        />
                        );
                      })}
                    </SortableContext>
                  </div>
                  <DragOverlay dropAnimation={null}>
                    {activeDragId && activeDragId.startsWith('product-') && (() => {
                      const productId = parseInt(activeDragId.replace('product-', ''));
                      const product = products.find((p: Product) => p.id === productId);
                      if (!product) return null;
                      return (
                        <div className="flex items-center justify-between p-4 bg-white border border-cn-warm rounded-lg shadow-lg opacity-90">
                          <div className="flex items-center space-x-3">
                            <GripVertical className="h-5 w-5 text-gray-400" />
                            <img 
                              src={getProductImage(product.name, product)} 
                              alt={product.name}
                              className="w-10 h-10 object-cover rounded"
                            />
                            <span className="font-medium">{productParts(product).item}</span>
                          </div>
                        </div>
                      );
                    })()}
                    {activeDragId && activeDragId.startsWith('category-') && (() => {
                      const categoryId = parseInt(activeDragId.replace('category-', ''));
                      const category = categories.find((c: Category) => c.id === categoryId);
                      if (!category) return null;
                      return (
                        <div className="flex items-center p-4 bg-white border border-cn-warm rounded-lg shadow-lg opacity-90">
                          <GripVertical className="h-5 w-5 text-gray-400 mr-3" />
                          <span className="mr-3"><CategoryIcon icon={category.icon} size="lg" /></span>
                          <span className="font-semibold">{category.name}</span>
                        </div>
                      );
                    })()}
                  </DragOverlay>
                </DndContext>
              )}
            </CardContent>
            <TypePriceSaveBar grid={typeGrid} />
          </Card>

          <CustomerTypesCard />
        </TabsContent>

        <TabsContent value="delivery" className="space-y-4">
          <Card>
            <CardHeader className="border-b border-gray-100 bg-gray-50/50">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" /> Fulfillment Options
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {orderWindowConfigLoading ? (
                <div className="text-center py-8">Loading settings...</div>
              ) : (
                <div className="space-y-6">

                  {/* DELIVERY SECTION */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Truck className="h-5 w-5 text-gray-700" />
                        <Label className="text-base font-semibold">Delivery</Label>
                      </div>
                      <Switch
                        id="delivery-enabled"
                        checked={deliveryEnabled}
                        onCheckedChange={(checked) => {
                          if (!checked && !pickupEnabled) {
                            toast({ description: "At least one fulfillment method must be enabled." });
                            return;
                          }
                          setDeliveryEnabled(checked);
                          if (!checked) {
                            setPickupFollowsDeliverySchedule(false);
                          }
                        }}
                      />
                    </div>

                    {deliveryEnabled && (
                      <div className="space-y-4 pl-7">
                        <div className="space-y-2">
                          <div className="flex items-center gap-1.5">
                            <Label>Order Windows</Label>
                            <InfoTooltip>How often delivery windows reset.</InfoTooltip>
                          </div>
                          <Select value={orderWindowFrequency} onValueChange={(v) => {
                              setOrderWindowFrequency(v);
                            }}>
                            <SelectTrigger data-testid="select-order-window-frequency">
                              <SelectValue placeholder="Select frequency" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="daily">Daily</SelectItem>
                              <SelectItem value="weekly">Weekly</SelectItem>
                              <SelectItem value="biweekly">Bi-weekly</SelectItem>
                              <SelectItem value="custom">Custom (select days)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Delivery zones switch (brief §5 row 10): offered for schedules with more
                            than one delivery day; once on, it can always be switched off. */}
                        {(orderWindowFrequency === 'custom' || orderWindowFrequency === 'daily' || deliveryDaysByZone) && (
                          <div className="space-y-2">
                            <div className="flex items-center gap-1.5">
                              <Label>Delivery days by customer</Label>
                              <InfoTooltip>With zones, a customer can choose only the days of the runs that reach their town or postal code area. Set up zones in Delivery Zones below.</InfoTooltip>
                            </div>
                            <RadioGroup
                              value={deliveryDaysByZone ? 'zone' : 'same'}
                              onValueChange={(v) => setDeliveryDaysByZone(v === 'zone')}
                              className="flex flex-wrap gap-4"
                            >
                              <div className="flex items-center gap-2">
                                <RadioGroupItem value="same" id="delivery-days-same" data-testid="radio-delivery-days-same" />
                                <Label htmlFor="delivery-days-same" className="font-normal">Same for everyone</Label>
                              </div>
                              <div className="flex items-center gap-2">
                                <RadioGroupItem
                                  value="zone"
                                  id="delivery-days-zone"
                                  disabled={orderWindowFrequency !== 'custom' && orderWindowFrequency !== 'daily'}
                                  data-testid="radio-delivery-days-zone"
                                />
                                <Label htmlFor="delivery-days-zone" className="font-normal">Vary by zone</Label>
                              </div>
                            </RadioGroup>
                          </div>
                        )}

                        {orderWindowFrequency === 'daily' && (
                          <div className="space-y-2">
                            <div className="flex items-center gap-1.5">
                              <Label>Cutoff</Label>
                              <InfoTooltip>How far before the delivery day orders must be placed.</InfoTooltip>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              {(() => {
                                const cutoffMode = deliveryLeadDays === 0 ? 'same' : deliveryLeadDays === 1 ? 'previous' : 'custom';
                                return (
                                  <>
                                    <Select
                                      value={cutoffMode}
                                      onValueChange={(v) => {
                                        if (v === 'same') setDeliveryLeadDays(0);
                                        else if (v === 'previous') setDeliveryLeadDays(1);
                                      }}
                                    >
                                      <SelectTrigger className="w-[160px]">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="same">Same day</SelectItem>
                                        <SelectItem value="previous">Previous day</SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <span className="text-sm text-gray-500">by</span>
                                    <div className="flex gap-1">
                                      <Select value={orderWindowCutoffHour.toString()} onValueChange={(v) => setOrderWindowCutoffHour(parseInt(v))}>
                                        <SelectTrigger className="w-[80px]">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {Array.from({ length: 24 }, (_, i) => {
                                            const h = i % 12 || 12;
                                            const ampm = i >= 12 ? 'PM' : 'AM';
                                            return <SelectItem key={i} value={i.toString()}>{h} {ampm}</SelectItem>;
                                          })}
                                        </SelectContent>
                                      </Select>
                                      <span className="self-center">:</span>
                                      <Select value={orderWindowCutoffMinute.toString()} onValueChange={(v) => setOrderWindowCutoffMinute(parseInt(v))}>
                                        <SelectTrigger className="w-[70px]">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="0">00</SelectItem>
                                          <SelectItem value="15">15</SelectItem>
                                          <SelectItem value="30">30</SelectItem>
                                          <SelectItem value="45">45</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        )}

                        {(orderWindowFrequency === 'weekly' || orderWindowFrequency === 'biweekly') && (
                          <div className="space-y-4">
                            <div className="space-y-2">
                              <Label>Delivery Day</Label>
                              <Select 
                                value={orderWindowCustomDays > 0 ? Math.floor(Math.log2(orderWindowCustomDays)).toString() : "1"} 
                                onValueChange={(v) => setOrderWindowCustomDays(1 << parseInt(v))}
                              >
                                <SelectTrigger data-testid="select-reset-day">
                                  <SelectValue placeholder="Select day" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="0">Sunday</SelectItem>
                                  <SelectItem value="1">Monday</SelectItem>
                                  <SelectItem value="2">Tuesday</SelectItem>
                                  <SelectItem value="3">Wednesday</SelectItem>
                                  <SelectItem value="4">Thursday</SelectItem>
                                  <SelectItem value="5">Friday</SelectItem>
                                  <SelectItem value="6">Saturday</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <div className="flex items-center gap-1.5">
                                <Label>Cutoff</Label>
                                <InfoTooltip>How far before the delivery day orders must be placed.</InfoTooltip>
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                {(() => {
                                  const cutoffMode = deliveryLeadDays === 0 ? 'same' : deliveryLeadDays === 1 ? 'previous' : 'custom';
                                  return (
                                    <>
                                      <Select
                                        value={cutoffMode}
                                        onValueChange={(v) => {
                                          if (v === 'same') setDeliveryLeadDays(0);
                                          else if (v === 'previous') setDeliveryLeadDays(1);
                                          else setDeliveryLeadDays(2);
                                        }}
                                      >
                                        <SelectTrigger className="w-[160px]">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="same">Same day</SelectItem>
                                          <SelectItem value="previous">Previous day</SelectItem>
                                          <SelectItem value="custom">Custom</SelectItem>
                                        </SelectContent>
                                      </Select>
                                      {cutoffMode === 'custom' && (
                                        <div className="flex items-center gap-1.5">
                                          <Select
                                            value={deliveryLeadDays.toString()}
                                            onValueChange={(v) => setDeliveryLeadDays(parseInt(v))}
                                          >
                                            <SelectTrigger className="w-[100px]">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {Array.from({ length: 5 }, (_, i) => i + 2).map((d) => (
                                                <SelectItem key={d} value={d.toString()}>
                                                  {d} days
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                          <span className="text-sm text-gray-500">before delivery date,</span>
                                        </div>
                                      )}
                                      <span className="text-sm text-gray-500">by</span>
                                      <div className="flex gap-1">
                                        <Select value={orderWindowCutoffHour.toString()} onValueChange={(v) => setOrderWindowCutoffHour(parseInt(v))}>
                                          <SelectTrigger className="w-[80px]">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {Array.from({ length: 24 }, (_, i) => {
                                              const h = i % 12 || 12;
                                              const ampm = i >= 12 ? 'PM' : 'AM';
                                              return <SelectItem key={i} value={i.toString()}>{h} {ampm}</SelectItem>;
                                            })}
                                          </SelectContent>
                                        </Select>
                                        <span className="self-center">:</span>
                                        <Select value={orderWindowCutoffMinute.toString()} onValueChange={(v) => setOrderWindowCutoffMinute(parseInt(v))}>
                                          <SelectTrigger className="w-[70px]">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="0">00</SelectItem>
                                            <SelectItem value="15">15</SelectItem>
                                            <SelectItem value="30">30</SelectItem>
                                            <SelectItem value="45">45</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>
                                    </>
                                  );
                                })()}
                              </div>
                            </div>
                          </div>
                        )}

                        {orderWindowFrequency === 'biweekly' && (
                          <div className="space-y-2">
                            <Label>Cycle Start Date</Label>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  className="w-full justify-start text-left font-normal"
                                  data-testid="button-anchor-date"
                                >
                                  <CalendarIcon className="mr-2 h-4 w-4" />
                                  {format(orderWindowAnchorDate, "PPP")}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                  mode="single"
                                  selected={orderWindowAnchorDate}
                                  onSelect={(date) => date && setOrderWindowAnchorDate(date)}
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                            <p className="text-xs text-gray-500">
                              Choose a date that falls on the first {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][orderWindowCustomDays > 0 ? Math.floor(Math.log2(orderWindowCustomDays)) : 1]} of your current two-week cycle.
                            </p>
                          </div>
                        )}

                        {orderWindowFrequency === 'custom' && (
                          <div className="space-y-6">
                            <div className="space-y-2">
                              <div className="flex items-center gap-1.5">
                                <Label>Delivery Days</Label>
                                <InfoTooltip>Select which days of the week deliveries are available.</InfoTooltip>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => {
                                  const isSelected = (orderWindowCustomDays & (1 << index)) !== 0;
                                  return (
                                    <Button
                                      key={day}
                                      type="button"
                                      variant={isSelected ? "default" : "outline"}
                                      size="sm"
                                      onClick={() => {
                                        if (isSelected) {
                                          setOrderWindowCustomDays(orderWindowCustomDays & ~(1 << index));
                                        } else {
                                          setOrderWindowCustomDays(orderWindowCustomDays | (1 << index));
                                        }
                                      }}
                                      className={isSelected ? "bg-cn-brand-blue hover:bg-cn-brand-blue-hover" : ""}
                                      data-testid={`button-day-${day.toLowerCase()}`}
                                    >
                                      {day}
                                    </Button>
                                  );
                                })}
                              </div>
                            </div>

                            <div className="space-y-2">
                              <div className="flex items-center gap-1.5">
                                <Label>Cutoff</Label>
                                <InfoTooltip>How far before the delivery day orders must be placed.</InfoTooltip>
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                {(() => {
                                  const getMaxCutoffDays = () => {
                                    if (orderWindowCustomDays <= 0) return 7;
                                    const activeDays: number[] = [];
                                    for (let i = 0; i < 7; i++) {
                                      if (orderWindowCustomDays & (1 << i)) activeDays.push(i);
                                    }
                                    if (activeDays.length <= 1) return 7;
                                    let minGap = 7;
                                    for (let i = 0; i < activeDays.length; i++) {
                                      const next = (i + 1) % activeDays.length;
                                      const gap = (activeDays[next] - activeDays[i] + 7) % 7;
                                      if (gap > 0 && gap < minGap) minGap = gap;
                                    }
                                    return Math.max(minGap - 1, 1);
                                  };
                                  const maxCutoff = getMaxCutoffDays();
                                  const cutoffMode = deliveryLeadDays === 0 ? 'same' : deliveryLeadDays === 1 ? 'previous' : 'custom';
                                  return (
                                    <>
                                      <Select
                                        value={cutoffMode}
                                        onValueChange={(v) => {
                                          if (v === 'same') setDeliveryLeadDays(0);
                                          else if (v === 'previous') setDeliveryLeadDays(1);
                                          else setDeliveryLeadDays(Math.min(2, maxCutoff));
                                        }}
                                      >
                                        <SelectTrigger className="w-[160px]" data-testid="select-delivery-cutoff-mode">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="same">Same day</SelectItem>
                                          <SelectItem value="previous">Previous day</SelectItem>
                                          {maxCutoff >= 2 && <SelectItem value="custom">Custom</SelectItem>}
                                        </SelectContent>
                                      </Select>
                                      {cutoffMode === 'custom' && maxCutoff >= 2 && (
                                        <div className="flex items-center gap-1.5">
                                          <Select
                                            value={deliveryLeadDays.toString()}
                                            onValueChange={(v) => setDeliveryLeadDays(parseInt(v))}
                                          >
                                            <SelectTrigger className="w-[100px]" data-testid="select-delivery-cutoff-custom-days">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {Array.from({ length: Math.max(maxCutoff - 1, 1) }, (_, i) => i + 2).map((d) => (
                                                <SelectItem key={d} value={d.toString()}>
                                                  {d} days
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                          <span className="text-sm text-gray-500">before delivery date,</span>
                                        </div>
                                      )}
                                      <span className="text-sm text-gray-500">by</span>
                                      <div className="flex gap-1">
                                        <Select
                                          value={orderWindowCutoffHour.toString()}
                                          onValueChange={(v) => setOrderWindowCutoffHour(parseInt(v))}
                                        >
                                          <SelectTrigger className="w-[72px]" data-testid="select-cutoff-hour">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {Array.from({ length: 24 }, (_, i) => (
                                              <SelectItem key={i} value={i.toString()}>
                                                {(i % 12 || 12).toString()}{i < 12 ? ' AM' : ' PM'}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                        <span className="self-center">:</span>
                                        <Select
                                          value={orderWindowCutoffMinute.toString()}
                                          onValueChange={(v) => setOrderWindowCutoffMinute(parseInt(v))}
                                        >
                                          <SelectTrigger className="w-[68px]" data-testid="select-cutoff-minute">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="0">00</SelectItem>
                                            <SelectItem value="15">15</SelectItem>
                                            <SelectItem value="30">30</SelectItem>
                                            <SelectItem value="45">45</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>
                                    </>
                                  );
                                })()}
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="space-y-3">
                          <div className="flex items-center gap-1.5">
                            <Label className="text-sm font-medium">Blackout Dates</Label>
                            <InfoTooltip>Block specific dates when delivery is unavailable (holidays, closures, etc.).</InfoTooltip>
                          </div>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className="gap-2">
                                <CalendarIcon className="h-4 w-4" />
                                Add Blackout Dates
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={deliveryBlackoutRangeStart || undefined}
                                onSelect={(date) => {
                                  if (!deliveryBlackoutRangeStart) {
                                    if (!date) return;
                                    setDeliveryBlackoutRangeStart(date);
                                  } else {
                                    const clickedDate = date || deliveryBlackoutRangeStart;
                                    const start = deliveryBlackoutRangeStart < clickedDate ? deliveryBlackoutRangeStart : clickedDate;
                                    const end = deliveryBlackoutRangeStart < clickedDate ? clickedDate : deliveryBlackoutRangeStart;
                                    const newDates: string[] = [];
                                    const current = new Date(start);
                                    while (current <= end) {
                                      const yyyy = current.getFullYear();
                                      const mm = String(current.getMonth() + 1).padStart(2, '0');
                                      const dd = String(current.getDate()).padStart(2, '0');
                                      newDates.push(`${yyyy}-${mm}-${dd}`);
                                      current.setDate(current.getDate() + 1);
                                    }
                                    const merged = Array.from(new Set([...deliveryBlackoutDates, ...newDates])).sort();
                                    setDeliveryBlackoutDates(merged);
                                    setDeliveryBlackoutRangeStart(null);
                                  }
                                }}
                                modifiers={{
                                  blackout: deliveryBlackoutDates.map(d => new Date(d + 'T12:00:00')),
                                  rangeStart: deliveryBlackoutRangeStart ? [deliveryBlackoutRangeStart] : [],
                                }}
                                modifiersStyles={{
                                  blackout: { backgroundColor: '#e0f2fe', color: '#0369a1', borderRadius: '50%', fontWeight: '600', border: '2px solid #0ea5e9' },
                                  rangeStart: { backgroundColor: '#3b82f6', color: 'white', borderRadius: '50%' },
                                }}
                                disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                              />
                              {deliveryBlackoutRangeStart && (
                                <p className="text-xs text-blue-600 mt-1 px-3 pb-2">Click another date to select a range, or click the same date to add a single day.</p>
                              )}
                            </PopoverContent>
                          </Popover>

                          {deliveryBlackoutDates.length > 0 && (
                            <div className="space-y-2">
                              <div className="flex flex-wrap gap-2">
                                {groupConsecutiveDates(deliveryBlackoutDates).map((group) => {
                                  const label = formatBlackoutChipLabel(group);
                                  return (
                                    <Badge key={group.start} variant="secondary" className="gap-1 pl-2.5 pr-1.5 py-1">
                                      {label}
                                      <button
                                        type="button"
                                        onClick={() => setDeliveryBlackoutDates(deliveryBlackoutDates.filter(dd => !group.dates.includes(dd)))}
                                        className="ml-1 rounded-full hover:bg-gray-300 p-0.5"
                                      >
                                        <span className="sr-only">Remove</span>
                                        <span className="text-xs leading-none">✕</span>
                                      </button>
                                    </Badge>
                                  );
                                })}
                              </div>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <button
                                    type="button"
                                    className="text-xs text-red-500 hover:text-red-700 underline"
                                  >
                                    Clear all blackout dates
                                  </button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Clear all blackout dates?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will remove all {deliveryBlackoutDates.length} blackout date{deliveryBlackoutDates.length !== 1 ? 's' : ''} from delivery. This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => setDeliveryBlackoutDates([])} className="bg-red-600 hover:bg-red-700">
                                      Clear All
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          )}
                        </div>

                        <div className="space-y-2 pt-2">
                          <div className="flex items-center gap-1.5">
                            <Label>Pre-order Range</Label>
                            <InfoTooltip>How far ahead customers can pre-order deliveries.</InfoTooltip>
                          </div>
                          <Select 
                            value="7" 
                            disabled
                          >
                            <SelectTrigger data-testid="select-delivery-max-lead-days">
                              <SelectValue placeholder="Select window" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="7">1 Week</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                          <div className="flex items-center gap-1.5 mb-2">
                            <h4 className="font-medium text-green-800">Customer Preview</h4>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <HelpCircle className="h-3.5 w-3.5 text-green-500 cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-xs">
                                  <p className="text-xs">This is an example of what your customers will see based on current settings.</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          <p className="text-sm text-green-700">
                            {(() => {
                              const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                              const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                              const getOrdinal = (d: number) => { if (d > 3 && d < 21) return 'th'; switch (d % 10) { case 1: return 'st'; case 2: return 'nd'; case 3: return 'rd'; default: return 'th'; } };

                              const now = new Date();
                              let deliveryDate: Date;

                              if (orderWindowFrequency === 'custom') {
                                const activeDays: number[] = [];
                                for (let i = 0; i < 7; i++) {
                                  if (orderWindowCustomDays & (1 << i)) activeDays.push(i);
                                }
                                if (activeDays.length === 0) activeDays.push(1);
                                deliveryDate = new Date(now);
                                let found = false;
                                for (let offset = 1; offset <= 8; offset++) {
                                  const check = new Date(now);
                                  check.setDate(now.getDate() + offset);
                                  if (activeDays.includes(check.getDay())) {
                                    deliveryDate = check;
                                    found = true;
                                    break;
                                  }
                                }
                                if (!found) deliveryDate.setDate(now.getDate() + 1);
                              } else if (orderWindowFrequency === 'weekly') {
                                const resetDay = orderWindowCustomDays > 0 ? Math.floor(Math.log2(orderWindowCustomDays)) : 1;
                                deliveryDate = new Date(now);
                                const diff = (resetDay - now.getDay() + 7) % 7;
                                deliveryDate.setDate(now.getDate() + (diff === 0 ? 7 : diff));
                              } else {
                                deliveryDate = new Date(now);
                                deliveryDate.setDate(now.getDate() + 1);
                              }

                              const cutoffDate = new Date(deliveryDate);
                              cutoffDate.setDate(deliveryDate.getDate() - deliveryLeadDays);
                              cutoffDate.setHours(orderWindowCutoffHour, orderWindowCutoffMinute, 0, 0);

                              const cutoffHourDisplay = orderWindowCutoffHour % 12 || 12;
                              const cutoffAmPm = orderWindowCutoffHour >= 12 ? 'PM' : 'AM';
                              const cutoffTimeStr = orderWindowCutoffMinute > 0
                                ? `${cutoffHourDisplay}:${orderWindowCutoffMinute.toString().padStart(2, '0')} ${cutoffAmPm}`
                                : `${cutoffHourDisplay} ${cutoffAmPm}`;

                              const deliveryStr = `${dayNames[deliveryDate.getDay()]} ${monthNames[deliveryDate.getMonth()]} ${deliveryDate.getDate()}${getOrdinal(deliveryDate.getDate())}`;
                              const cutoffDateStr = deliveryLeadDays === 0
                                ? null
                                : `${dayNames[cutoffDate.getDay()]} ${monthNames[cutoffDate.getMonth()]} ${cutoffDate.getDate()}${getOrdinal(cutoffDate.getDate())}`;

                              return (
                                <>
                                  Orders for <strong>{deliveryStr}</strong> delivery must be placed by <strong>{cutoffTimeStr}</strong>{cutoffDateStr ? <> on <strong>{cutoffDateStr}</strong></> : ''}.
                                </>
                              );
                            })()}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  <Separator />

                  {/* PICKUP SECTION */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-gray-700" />
                        <Label className="text-base font-semibold">Pickup</Label>
                      </div>
                      <Switch
                        id="pickup-enabled"
                        checked={pickupEnabled}
                        onCheckedChange={(checked) => {
                          if (!checked && !deliveryEnabled) {
                            toast({ description: "At least one fulfillment method must be enabled." });
                            return;
                          }
                          setPickupEnabled(checked);
                          if (!checked) {
                            setPickupFollowsDeliverySchedule(false);
                          }
                        }}
                      />
                    </div>

                    {pickupEnabled && (
                      <div className="space-y-4 pl-7">
                        {deliveryEnabled && (
                          <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <Checkbox
                              id="pickup-follows-delivery"
                              checked={pickupFollowsDeliverySchedule}
                              onCheckedChange={(checked) => {
                                setPickupFollowsDeliverySchedule(checked === true);
                                if (checked === true) {
                                  setPickupAvailableDays(orderWindowCustomDays);
                                  setPickupPrepDays(deliveryLeadDays);
                                  setPickupCutoffHour(orderWindowCutoffHour);
                                  setPickupCutoffMinute(orderWindowCutoffMinute);
                                  setPickupBlackoutDates([...deliveryBlackoutDates]);
                                  setPickupMaxLeadDays(deliveryMaxLeadDays);
                                }
                              }}
                            />
                            <div className="space-y-1">
                              <Label htmlFor="pickup-follows-delivery" className="text-sm font-medium cursor-pointer">
                                Same as delivery
                              </Label>
                              <p className="text-xs text-gray-500">
                                Use the same cutoff, timing, and blackout dates as delivery.
                              </p>
                            </div>
                          </div>
                        )}

                        <div className={pickupFollowsDeliverySchedule && deliveryEnabled ? "opacity-50 pointer-events-none space-y-6" : "space-y-6"}>
                              <div className="space-y-3">
                                <div className="flex items-center gap-1.5">
                                  <Label className="text-sm font-medium">Pickup Days</Label>
                                  <InfoTooltip>Select which days of the week customers can choose for pickup.</InfoTooltip>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => {
                                    const isActive = (pickupAvailableDays & (1 << index)) !== 0;
                                    return (
                                      <Button
                                        key={day}
                                        type="button"
                                        variant={isActive ? "default" : "outline"}
                                        size="sm"
                                        onClick={() => {
                                          if (isActive) {
                                            setPickupAvailableDays(pickupAvailableDays & ~(1 << index));
                                          } else {
                                            setPickupAvailableDays(pickupAvailableDays | (1 << index));
                                          }
                                        }}
                                        className={isActive ? "bg-cn-brand-blue hover:bg-cn-brand-blue-hover" : ""}
                                        data-testid={`button-pickup-day-${day.toLowerCase()}`}
                                      >
                                        {day}
                                      </Button>
                                    );
                                  })}
                                </div>
                              </div>

                              <div className="space-y-2">
                                <div className="flex items-center gap-1.5">
                                  <Label>Cutoff</Label>
                                  <InfoTooltip>How far before the pickup day orders must be placed.</InfoTooltip>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  {(() => {
                                    const getMaxPickupCutoffDays = () => {
                                      if (pickupAvailableDays <= 0) return 7;
                                      const activeDays: number[] = [];
                                      for (let i = 0; i < 7; i++) {
                                        if (pickupAvailableDays & (1 << i)) activeDays.push(i);
                                      }
                                      if (activeDays.length <= 1) return 7;
                                      let minGap = 7;
                                      for (let i = 0; i < activeDays.length; i++) {
                                        const next = (i + 1) % activeDays.length;
                                        const gap = (activeDays[next] - activeDays[i] + 7) % 7;
                                        if (gap > 0 && gap < minGap) minGap = gap;
                                      }
                                      return Math.max(minGap - 1, 1);
                                    };
                                    const maxCutoff = getMaxPickupCutoffDays();
                                    const cutoffMode = pickupPrepDays === 0 ? 'same' : pickupPrepDays === 1 ? 'previous' : 'custom';
                                    return (
                                      <>
                                        <Select
                                          value={cutoffMode}
                                          onValueChange={(v) => {
                                            if (v === 'same') setPickupPrepDays(0);
                                            else if (v === 'previous') setPickupPrepDays(1);
                                            else setPickupPrepDays(Math.min(2, maxCutoff));
                                          }}
                                        >
                                          <SelectTrigger className="w-[160px]" data-testid="select-pickup-cutoff-mode">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="same">Same day</SelectItem>
                                            <SelectItem value="previous">Previous day</SelectItem>
                                            {maxCutoff >= 2 && <SelectItem value="custom">Custom</SelectItem>}
                                          </SelectContent>
                                        </Select>
                                        {cutoffMode === 'custom' && maxCutoff >= 2 && (
                                          <div className="flex items-center gap-1.5">
                                            <Select
                                              value={pickupPrepDays.toString()}
                                              onValueChange={(v) => setPickupPrepDays(parseInt(v))}
                                            >
                                              <SelectTrigger className="w-[100px]" data-testid="select-pickup-cutoff-custom-days">
                                                <SelectValue />
                                              </SelectTrigger>
                                              <SelectContent>
                                                {Array.from({ length: Math.max(maxCutoff - 1, 1) }, (_, i) => i + 2).map((d) => (
                                                  <SelectItem key={d} value={d.toString()}>
                                                    {d} days
                                                  </SelectItem>
                                                ))}
                                              </SelectContent>
                                            </Select>
                                            <span className="text-sm text-gray-500">before pickup date,</span>
                                          </div>
                                        )}
                                        <span className="text-sm text-gray-500">by</span>
                                        <div className="flex gap-1">
                                          <Select
                                            value={pickupCutoffHour.toString()}
                                            onValueChange={(v) => setPickupCutoffHour(parseInt(v))}
                                          >
                                            <SelectTrigger className="w-[72px]" data-testid="select-pickup-cutoff-hour">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {Array.from({ length: 24 }, (_, i) => (
                                                <SelectItem key={i} value={i.toString()}>
                                                  {(i % 12 || 12).toString()}{i < 12 ? ' AM' : ' PM'}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                          <span className="self-center">:</span>
                                          <Select
                                            value={pickupCutoffMinute.toString()}
                                            onValueChange={(v) => setPickupCutoffMinute(parseInt(v))}
                                          >
                                            <SelectTrigger className="w-[68px]" data-testid="select-pickup-cutoff-minute">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="0">00</SelectItem>
                                              <SelectItem value="15">15</SelectItem>
                                              <SelectItem value="30">30</SelectItem>
                                              <SelectItem value="45">45</SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </div>
                                      </>
                                    );
                                  })()}
                                </div>
                              </div>

                              <div className="space-y-3">
                              <div className="flex items-center gap-1.5">
                                <Label className="text-sm font-medium">Blackout Dates</Label>
                                <InfoTooltip>Block specific dates when pickup is unavailable (holidays, closures, etc.).</InfoTooltip>
                              </div>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button variant="outline" className="gap-2">
                                    <CalendarIcon className="h-4 w-4" />
                                    Add Blackout Dates
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                  <Calendar
                                    mode="single"
                                    selected={pickupBlackoutRangeStart || undefined}
                                    onSelect={(date) => {
                                      if (!pickupBlackoutRangeStart) {
                                        if (!date) return;
                                        setPickupBlackoutRangeStart(date);
                                      } else {
                                        const clickedDate = date || pickupBlackoutRangeStart;
                                        const start = pickupBlackoutRangeStart < clickedDate ? pickupBlackoutRangeStart : clickedDate;
                                        const end = pickupBlackoutRangeStart < clickedDate ? clickedDate : pickupBlackoutRangeStart;
                                        const newDates: string[] = [];
                                        const current = new Date(start);
                                        while (current <= end) {
                                          const yyyy = current.getFullYear();
                                          const mm = String(current.getMonth() + 1).padStart(2, '0');
                                          const dd = String(current.getDate()).padStart(2, '0');
                                          newDates.push(`${yyyy}-${mm}-${dd}`);
                                          current.setDate(current.getDate() + 1);
                                        }
                                        const merged = Array.from(new Set([...pickupBlackoutDates, ...newDates])).sort();
                                        setPickupBlackoutDates(merged);
                                        setPickupBlackoutRangeStart(null);
                                      }
                                    }}
                                    modifiers={{
                                      blackout: pickupBlackoutDates.map(d => new Date(d + 'T12:00:00')),
                                      rangeStart: pickupBlackoutRangeStart ? [pickupBlackoutRangeStart] : [],
                                    }}
                                    modifiersStyles={{
                                      blackout: { backgroundColor: '#e0f2fe', color: '#0369a1', borderRadius: '50%', fontWeight: '600', border: '2px solid #0ea5e9' },
                                      rangeStart: { backgroundColor: '#3b82f6', color: 'white', borderRadius: '50%' },
                                    }}
                                    disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                                  />
                                  {pickupBlackoutRangeStart && (
                                    <p className="text-xs text-blue-600 mt-1 px-3 pb-2">Click another date to select a range, or click the same date to add a single day.</p>
                                  )}
                                </PopoverContent>
                              </Popover>

                              {pickupBlackoutDates.length > 0 && (
                                <div className="space-y-2">
                                  <div className="flex flex-wrap gap-2">
                                    {groupConsecutiveDates(pickupBlackoutDates).map((group) => {
                                      const label = formatBlackoutChipLabel(group);
                                      return (
                                        <Badge key={group.start} variant="secondary" className="gap-1 pl-2.5 pr-1.5 py-1">
                                          {label}
                                          <button
                                            type="button"
                                            onClick={() => setPickupBlackoutDates(pickupBlackoutDates.filter(dd => !group.dates.includes(dd)))}
                                            className="ml-1 rounded-full hover:bg-gray-300 p-0.5"
                                          >
                                            <span className="sr-only">Remove</span>
                                            <span className="text-xs leading-none">✕</span>
                                          </button>
                                        </Badge>
                                      );
                                    })}
                                  </div>
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <button
                                        type="button"
                                        className="text-xs text-red-500 hover:text-red-700 underline"
                                      >
                                        Clear all blackout dates
                                      </button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>Clear all blackout dates?</AlertDialogTitle>
                                        <AlertDialogDescription>
                                          This will remove all {pickupBlackoutDates.length} blackout date{pickupBlackoutDates.length !== 1 ? 's' : ''} from pickup. This action cannot be undone.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                        <AlertDialogAction onClick={() => setPickupBlackoutDates([])} className="bg-red-600 hover:bg-red-700">
                                          Clear All
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                </div>
                              )}
                            </div>

                              <div className="space-y-2">
                              <div className="flex items-center gap-1.5">
                                <Label>Pre-order Range</Label>
                                <InfoTooltip>How far ahead customers can pre-order pickups.</InfoTooltip>
                              </div>
                              <Select 
                                value="7" 
                                disabled
                              >
                                <SelectTrigger data-testid="select-pickup-max-lead-days">
                                  <SelectValue placeholder="Select window" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="7">1 Week</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                              <div className="flex items-center gap-1.5 mb-2">
                                <h4 className="font-medium text-green-800">Customer Preview</h4>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <HelpCircle className="h-3.5 w-3.5 text-green-500 cursor-help" />
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="max-w-xs">
                                      <p className="text-xs">This is an example of what your customers will see based on current settings.</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                              <p className="text-sm text-green-700">
                                {(() => {
                                  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                  const cutoffHourDisplay = pickupCutoffHour % 12 || 12;
                                  const cutoffAmPm = pickupCutoffHour >= 12 ? 'PM' : 'AM';
                                  const cutoffTimeStr = pickupCutoffMinute > 0 
                                    ? `${cutoffHourDisplay}:${pickupCutoffMinute.toString().padStart(2, '0')} ${cutoffAmPm}` 
                                    : `${cutoffHourDisplay} ${cutoffAmPm}`;
                                  
                                  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                                  const getOrdinal = (d: number) => { if (d > 3 && d < 21) return 'th'; switch (d % 10) { case 1: return 'st'; case 2: return 'nd'; case 3: return 'rd'; default: return 'th'; } };

                                  const activeDayIndices: number[] = [];
                                  for (let i = 0; i < 7; i++) {
                                    if (pickupAvailableDays & (1 << i)) activeDayIndices.push(i);
                                  }
                                  const activeDayNames = activeDayIndices.map(i => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][i]);

                                  const now = new Date();
                                  let pickupDate = new Date(now);
                                  if (activeDayIndices.length > 0) {
                                    for (let offset = 1; offset <= 8; offset++) {
                                      const check = new Date(now);
                                      check.setDate(now.getDate() + offset);
                                      if (activeDayIndices.includes(check.getDay())) {
                                        pickupDate = check;
                                        break;
                                      }
                                    }
                                  } else {
                                    pickupDate.setDate(now.getDate() + 1);
                                  }

                                  const pickupStr = `${dayNames[pickupDate.getDay()]} ${monthNames[pickupDate.getMonth()]} ${pickupDate.getDate()}${getOrdinal(pickupDate.getDate())}`;
                                  const cutoffDate = new Date(pickupDate);
                                  cutoffDate.setDate(pickupDate.getDate() - pickupPrepDays);
                                  const cutoffDateStr = pickupPrepDays === 0
                                    ? null
                                    : `${dayNames[cutoffDate.getDay()]} ${monthNames[cutoffDate.getMonth()]} ${cutoffDate.getDate()}${getOrdinal(cutoffDate.getDate())}`;
                                  
                                  return (
                                    <>
                                      Orders for <strong>{pickupStr}</strong> pickup must be placed by <strong>{cutoffTimeStr}</strong>{cutoffDateStr ? <> on <strong>{cutoffDateStr}</strong></> : ''}.
                                    </>
                                  );
                                })()}
                              </p>
                            </div>
                        </div>
                      </div>
                    )}
                  </div>

                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex items-center gap-4">
            <Button
              onClick={() => {
                updateOrderWindowMutation.mutate({
                  enabled: true,
                  frequency: orderWindowFrequency,
                  cutoffHour: orderWindowCutoffHour,
                  cutoffMinute: orderWindowCutoffMinute,
                  customDays: orderWindowCustomDays,
                  anchorStart: orderWindowAnchorDate.toISOString(),
                  pickupSchedulingEnabled: pickupEnabled,
                  pickupAvailableDays,
                  pickupBlackoutDates,
                  pickupMaxLeadDays: 7,
                  deliveryLeadDays,
                  deliveryMaxLeadDays: 7,
                  deliveryBlackoutDates,
                  deliveryEnabled,
                  pickupEnabled,
                  pickupFollowsDeliverySchedule,
                  pickupCutoffHour,
                  pickupCutoffMinute,
                  pickupPrepDays,
                  deliveryDaysByZone,
                });
              }}
              disabled={updateOrderWindowMutation.isPending}
              className="bg-cn-brand-blue hover:bg-cn-brand-blue-hover"
              data-testid="button-save-delivery-settings"
            >
              {updateOrderWindowMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Fulfillment Settings"
              )}
            </Button>
          </div>

          <DeliveryZonesCard />
        </TabsContent>

        <TabsContent value="admin" className="space-y-4">
          <Card>
            <CardHeader className="border-b border-gray-100 bg-gray-50/50">
              <div>
                <CardTitle>Order Preferences</CardTitle>
                <CardDescription className="mt-[5px] mb-[5px]">
                  Configure how orders behave when edited by admins.
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="auto-change-notes">Auto Change Notes</Label>
                  <p className="text-sm text-gray-500">
                    Automatically add a note to orders when an admin changes item quantities or removes items.
                  </p>
                </div>
                <Switch
                  id="auto-change-notes"
                  checked={autoChangeNotes}
                  onCheckedChange={async (checked) => {
                    setAutoChangeNotes(checked);
                    try {
                      await apiRequest("PUT", "/api/order-window-config", {
                        autoChangeNotes: checked
                      });
                      queryClient.invalidateQueries({ queryKey: ['/api/order-window-config'] });
                      toast({
                        title: checked ? "Auto change notes enabled" : "Auto change notes disabled",
                      });
                    } catch (error) {
                      setAutoChangeNotes(!checked);
                      toast({ title: "Failed to update setting", variant: "destructive" });
                    }
                  }}
                />
              </div>
            </CardContent>
          </Card>

          <PickSheetSettingsCard />

          <Card>
            <CardHeader className="border-b border-gray-100 bg-gray-50/50">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle>User Management</CardTitle>
                  <CardDescription className="mt-[5px] mb-[5px]">
                    Manage user accounts and permissions. Only Admin users can add, edit, or remove users.
                  </CardDescription>
                </div>
                <Button 
                  size="icon"
                  className="bg-cn-brand-green hover:bg-[hsl(140,27%,88%)] hover:text-cn-brand-green" 
                  onClick={() => {
                    setEditingUser(null);
                    userForm.reset({
                      username: "",
                      email: "",
                      password: "",
                      role: "staff",
                      isActive: true
                    });
                    setUserDialogOpen(true);
                  }}
                  data-testid="button-add-user"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {usersLoading ? (
                <div className="text-center py-8">Loading users...</div>
              ) : users.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No users found. Add the first user to get started.
                </div>
              ) : (
                <Table>
                  <TableHeader className="bg-gray-50">
                    <TableRow>
                      <TableHead className="text-xs font-medium text-gray-500 uppercase tracking-wider">Name</TableHead>
                      <TableHead className="text-xs font-medium text-gray-500 uppercase tracking-wider">Email</TableHead>
                      <TableHead className="text-xs font-medium text-gray-500 uppercase tracking-wider">Role</TableHead>
                      <TableHead className="text-xs font-medium text-gray-500 uppercase tracking-wider">Status</TableHead>
                      <TableHead className="text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</TableHead>
                      <TableHead className="text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[...users].sort((a, b) => {
                      const roleOrder = { admin: 0, staff: 1 };
                      return (roleOrder[a.role as keyof typeof roleOrder] ?? 2) - (roleOrder[b.role as keyof typeof roleOrder] ?? 2);
                    }).map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">
                          <button
                            onClick={() => handleEditUser(user)}
                            className="text-cn-brand-blue hover:text-cn-brand-blue-hover hover:underline cursor-pointer text-left"
                            data-testid={`button-edit-user-${user.id}`}
                          >
                            {user.username}
                          </button>
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {user.email || <span className="text-gray-400 italic">Not set</span>}
                        </TableCell>
                        <TableCell>
                          <span className={`${
                            user.role === 'admin' 
                              ? "bg-blue-100 text-cn-brand-blue" 
                              : "bg-gray-100 text-gray-600"
                          } px-2 py-1 text-xs rounded`}>
                            {user.role}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`${
                            user.isActive 
                              ? "bg-green-100 text-green-700" 
                              : "bg-amber-100 text-amber-700"
                          } px-2 py-1 text-xs rounded`}>
                            {user.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Never'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {currentUserRole === 'admin' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleResetPassword(user)}
                                className="text-gray-500 hover:text-cn-brand-blue p-1 h-auto"
                                title="Reset Password"
                                data-testid={`button-reset-password-${user.id}`}
                              >
                                <Key className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditUser(user)}
                              className="text-gray-500 hover:text-cn-brand-blue p-1 h-auto"
                              title="Edit User"
                              data-testid={`button-edit-user-icon-${user.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteUser(user.id)}
                              className="text-gray-500 hover:text-red-600 p-1 h-auto"
                              title="Delete User"
                              data-testid={`button-delete-user-${user.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Integrations — merged in from the former standalone Integrations tab */}
          <QuickBooksAuth />
          <QuickBooksSync />

        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          <HistoryTab />
        </TabsContent>

        {kioskEnabled && (
        <TabsContent value="kiosk" className="space-y-4">
          <Card>
            <CardHeader className="border-b border-gray-100 bg-gray-50/50">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <span className="text-xl">📱</span> Kiosk Mode
                </CardTitle>
                <CardDescription className="mt-[5px] mb-[5px]">
                  Configure kiosk mode for tablet-based ordering at your location. Set a PIN to unlock kiosk mode on shared devices.
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-6">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="kiosk-pin-tab">Kiosk PIN (4-6 digits)</Label>
                    <div className="relative">
                      <Input
                        id="kiosk-pin-tab"
                        type={showKioskPin ? "text" : "password"}
                        value={kioskPin}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                          setKioskPin(value);
                        }}
                        placeholder="Enter 4-6 digit PIN"
                        className="pr-10"
                        data-testid="input-kiosk-pin-tab"
                      />
                      <button
                        type="button"
                        onClick={() => setShowKioskPin(!showKioskPin)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showKioskPin ? "Hide" : "Show"}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500">
                      Staff will enter this PIN to unlock kiosk mode on the tablet.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="kiosk-timeout-tab">Session Timeout</Label>
                    <Select value={kioskTimeout} onValueChange={setKioskTimeout}>
                      <SelectTrigger data-testid="select-kiosk-timeout-tab">
                        <SelectValue placeholder="Select timeout" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="15">15 minutes</SelectItem>
                        <SelectItem value="30">30 minutes</SelectItem>
                        <SelectItem value="60">1 hour</SelectItem>
                        <SelectItem value="120">2 hours</SelectItem>
                        <SelectItem value="240">4 hours</SelectItem>
                        <SelectItem value="480">8 hours (full day)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">
                      Kiosk will lock automatically after this period of inactivity.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <Button
                    onClick={() => {
                      if (kioskPin.length < 4) {
                        toast({
                          title: "Invalid PIN",
                          description: "PIN must be at least 4 digits",
                          variant: "destructive"
                        });
                        return;
                      }
                      setKioskPinMutation.mutate({
                        pin: kioskPin,
                        sessionTimeout: parseInt(kioskTimeout)
                      });
                    }}
                    disabled={setKioskPinMutation.isPending || kioskPin.length < 4}
                    className="bg-cn-brand-blue hover:bg-cn-brand-blue-hover"
                    data-testid="button-save-kiosk-pin-tab"
                  >
                    {setKioskPinMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      "Save Kiosk Settings"
                    )}
                  </Button>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-800 mb-2">How Kiosk Mode Works</h4>
                  <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                    <li>Staff enters the PIN to unlock the kiosk on a shared tablet</li>
                    <li>Customers can enter their phone number to look up their account</li>
                    <li>New customers can register with just their name and phone</li>
                    <li>After ordering, customers specify their pickup location (bay door, parking spot)</li>
                    <li>Kiosk auto-locks after the timeout period for security</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="border-b border-gray-100 bg-gray-50/50">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Tablet className="h-5 w-5" /> Launch & Help
                </CardTitle>
                <CardDescription className="mt-[5px] mb-[5px]">
                  Launch kiosk mode or view the setup guide.
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={() => {
                    const kioskUrl = organization?.slug ? `/org/${organization.slug}/kiosk` : '/kiosk';
                    window.open(kioskUrl, '_blank');
                  }}
                  className="bg-cn-brand-blue hover:bg-cn-brand-blue-hover"
                  data-testid="button-launch-kiosk"
                >
                  <Tablet className="mr-2 h-4 w-4" />
                  Launch Kiosk
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    const helpUrl = organization?.slug ? `/org/${organization.slug}/admin/kiosk-help` : '/admin/kiosk-help';
                    window.open(helpUrl, '_blank');
                  }}
                  data-testid="button-kiosk-help"
                >
                  <HelpCircle className="mr-2 h-4 w-4" />
                  Kiosk Setup Guide
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        <TabsContent value="notifications" className="space-y-4">
          <Card>
            <CardHeader className="border-b border-gray-100 bg-gray-50/50">
              <div>
                <CardTitle className="flex items-center gap-2">
                  Default Notification Settings
                </CardTitle>
                <CardDescription className="mt-1">
                  These defaults apply to new accounts and any account that hasn't customized its own settings. Available template variables: <code className="text-xs bg-gray-100 px-1 rounded">[account]</code>, <code className="text-xs bg-gray-100 px-1 rounded">[active order deadline]</code>, <code className="text-xs bg-gray-100 px-1 rounded">[delivery day]</code>, <code className="text-xs bg-gray-100 px-1 rounded">[org-url]</code>
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {notificationDefaultsLoading ? (
                <div className="text-center py-8">Loading notification defaults...</div>
              ) : (
                <div className="space-y-8">

                  {/* SMS Section */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b pb-2">
                      <Label className="text-base font-semibold">SMS Reminders</Label>
                      <Switch
                        checked={notifSmsEnabled}
                        onCheckedChange={setNotifSmsEnabled}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Default Message Template</Label>
                      <Textarea
                        value={notifSmsTemplate}
                        onChange={(e) => setNotifSmsTemplate(e.target.value)}
                        placeholder="Leave blank to use built-in default. Use [account], [active order deadline], [delivery day], [org-url]."
                        rows={3}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>First Reminder</Label>
                        <div className="flex gap-2">
                          <Select value={notifSmsFirstDay} onValueChange={setNotifSmsFirstDay}>
                            <SelectTrigger className="flex-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {['sunday','monday','tuesday','wednesday','thursday','friday','saturday'].map(d => (
                                <SelectItem key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select value={notifSmsFirstTime} onValueChange={setNotifSmsFirstTime}>
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {timeOptions.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Second Reminder</Label>
                        <div className="flex gap-2">
                          <Select value={notifSmsSecondDay} onValueChange={setNotifSmsSecondDay}>
                            <SelectTrigger className="flex-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {['sunday','monday','tuesday','wednesday','thursday','friday','saturday'].map(d => (
                                <SelectItem key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select value={notifSmsSecondTime} onValueChange={setNotifSmsSecondTime}>
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {timeOptions.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <Label>Order Receipts via SMS</Label>
                      <Switch
                        checked={notifSmsOrderReceipts}
                        onCheckedChange={setNotifSmsOrderReceipts}
                      />
                    </div>
                  </div>

                  {/* Email Section */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b pb-2">
                      <Label className="text-base font-semibold">Email Reminders</Label>
                      <Switch
                        checked={notifEmailEnabled}
                        onCheckedChange={setNotifEmailEnabled}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Default Message Template</Label>
                      <Textarea
                        value={notifEmailTemplate}
                        onChange={(e) => setNotifEmailTemplate(e.target.value)}
                        placeholder="Leave blank to use built-in default. Use [account], [active order deadline], [delivery day], [org-url]."
                        rows={4}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>First Reminder</Label>
                        <div className="flex gap-2">
                          <Select value={notifEmailFirstDay} onValueChange={setNotifEmailFirstDay}>
                            <SelectTrigger className="flex-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {['sunday','monday','tuesday','wednesday','thursday','friday','saturday'].map(d => (
                                <SelectItem key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select value={notifEmailFirstTime} onValueChange={setNotifEmailFirstTime}>
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {timeOptions.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Second Reminder</Label>
                        <div className="flex gap-2">
                          <Select value={notifEmailSecondDay} onValueChange={setNotifEmailSecondDay}>
                            <SelectTrigger className="flex-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {['sunday','monday','tuesday','wednesday','thursday','friday','saturday'].map(d => (
                                <SelectItem key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select value={notifEmailSecondTime} onValueChange={setNotifEmailSecondTime}>
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {timeOptions.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <Label>Order Receipts via Email</Label>
                      <Switch
                        checked={notifEmailOrderReceipts}
                        onCheckedChange={setNotifEmailOrderReceipts}
                      />
                    </div>
                  </div>

                  <Button
                    onClick={handleSaveNotificationDefaults}
                    disabled={saveNotificationDefaultsMutation.isPending}
                    className="bg-cn-brand-green hover:bg-cn-brand-green-hover"
                  >
                    {saveNotificationDefaultsMutation.isPending ? "Saving..." : "Save Defaults"}
                  </Button>

                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="border-b border-gray-100 bg-gray-50/50">
              <div>
                <CardTitle className="flex items-center gap-2">
                  Market Report
                </CardTitle>
                <CardDescription className="mt-1">
                  An optional notice shown at the top of the customer order page. Use it to keep clients informed about what's currently affecting market prices (weather, supply, seasonal swings) — a passive resource they can check anytime, instead of a mass text. Off by default.
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {marketReportLoading ? (
                <div className="text-center py-8">Loading market report...</div>
              ) : (
                <div className="space-y-6">
                  <div className="flex items-center justify-between border-b pb-3">
                    <div className="space-y-1">
                      <Label className="text-base font-semibold">Show market report to customers</Label>
                      <p className="text-sm text-muted-foreground">
                        When on, clients see a Market Report card at the top of the order page.
                      </p>
                    </div>
                    <Switch
                      checked={marketReportEnabled}
                      onCheckedChange={setMarketReportEnabled}
                      data-testid="switch-market-report-enabled"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Title</Label>
                    <Input
                      value={marketReportTitle}
                      onChange={(e) => setMarketReportTitle(e.target.value)}
                      placeholder="Market Report"
                      maxLength={80}
                      data-testid="input-market-report-title"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Report</Label>
                    <Textarea
                      value={marketReportBody}
                      onChange={(e) => setMarketReportBody(e.target.value)}
                      placeholder="e.g. Heavy rain in California is tightening leafy-green supply; expect lettuce and broccoli prices up 15–20% this week. Citrus remains steady."
                      rows={10}
                      maxLength={5000}
                      data-testid="textarea-market-report-body"
                    />
                    <p className="text-xs text-muted-foreground">
                      Line breaks are preserved. {marketReport?.contentUpdatedAt && (
                        <>Last edited {format(new Date(marketReport.contentUpdatedAt), 'MMM d, yyyy')}.</>
                      )}
                    </p>
                  </div>

                  <Button
                    onClick={handleSaveMarketReport}
                    disabled={saveMarketReportMutation.isPending}
                    className="bg-cn-brand-green hover:bg-cn-brand-green-hover"
                    data-testid="button-save-market-report"
                  >
                    {saveMarketReportMutation.isPending ? "Saving..." : "Save Market Report"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Order Alerts — merged in from the former standalone Order Alerts tab */}
          <Card>
            <CardHeader className="border-b border-gray-100 bg-gray-50/50">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Order Alerts</CardTitle>
                  <CardDescription className="mt-1">
                    Get a text or an email when orders come in. Add the team members who should hear about it, and when.
                  </CardDescription>
                </div>
                <Button onClick={openAddAlert} size="sm" className="shrink-0">
                  <Plus className="h-4 w-4 mr-1" />
                  Add Subscriber
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              {alertsLoading ? (
                <div className="text-center py-8 text-gray-500">Loading...</div>
              ) : alertSubscriptions.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  No subscribers yet. Add a team member to receive order alerts.
                </div>
              ) : (
                <div className="space-y-3">
                  {alertSubscriptions.map((sub: any) => (
                    <div key={sub.id} className="border rounded-lg p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="font-medium text-sm">{sub.name}</div>
                        <div className="flex items-center gap-1">
                          <Switch
                            checked={sub.isActive}
                            onCheckedChange={(checked) =>
                              updateAlertMutation.mutate({ id: sub.id, data: { isActive: checked } })
                            }
                          />
                          <Button variant="ghost" size="sm" onClick={() => testAlertMutation.mutate(sub.id)}>
                            Test
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openEditAlert(sub)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (confirm(`Remove ${sub.name}?`)) deleteAlertMutation.mutate(sub.id);
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500">
                        {[sub.phone ? formatAlertPhone(sub.phone) : null, sub.email].filter(Boolean).join(" · ")}
                      </div>
                      <div className="flex flex-wrap gap-2 text-xs">
                        <span className={sub.alertOnNewOrder ? "text-green-600" : "text-gray-400"}>
                          {sub.alertOnNewOrder ? "✓" : "✗"} New orders
                        </span>
                        <span className={sub.alertOnOrderUpdate ? "text-green-600" : "text-gray-400"}>
                          {sub.alertOnOrderUpdate ? "✓" : "✗"} Updates
                        </span>
                        <span className={sub.alertOnOrderCancel ? "text-green-600" : "text-gray-400"}>
                          {sub.alertOnOrderCancel ? "✓" : "✗"} Cancellations
                        </span>
                      </div>
                      <div className="text-xs text-gray-400">{describeActiveWindow(sub)}</div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        </div>
      </Tabs>

      {/* Order Alert Subscriber Dialog */}
      <Dialog open={alertDialogOpen} onOpenChange={setAlertDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAlert ? "Edit Subscriber" : "Add Subscriber"}</DialogTitle>
            <DialogDescription>Who hears about order activity — by text, by email, or both.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label>Name</Label>
              <Input
                placeholder="e.g. Carlos - Warehouse"
                value={alertName}
                onChange={(e) => setAlertName(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>Phone number <span className="font-normal text-gray-500">(for texts)</span></Label>
              <Input
                placeholder="(226) 555-0100"
                value={alertPhone}
                onChange={(e) => setAlertPhone(e.target.value)}
                data-testid="input-alert-phone"
              />
            </div>
            <div className="space-y-1">
              <Label>Email <span className="font-normal text-gray-500">(for emails)</span></Label>
              <Input
                type="email"
                placeholder="carlos@example.com"
                value={alertEmail}
                onChange={(e) => setAlertEmail(e.target.value)}
                data-testid="input-alert-email"
              />
              <p className="text-xs text-gray-500">Fill in one or both.</p>
            </div>
            <div className="space-y-2">
              <Label>Alert on events</Label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={alertOnNew} onCheckedChange={(c) => setAlertOnNew(!!c)} />
                  <span className="text-sm">New orders</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={alertOnUpdate} onCheckedChange={(c) => setAlertOnUpdate(!!c)} />
                  <span className="text-sm">Order updates</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={alertOnCancel} onCheckedChange={(c) => setAlertOnCancel(!!c)} />
                  <span className="text-sm">Order cancellations</span>
                </label>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Days</Label>
              <div className="flex flex-wrap gap-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => {
                  const on = (alertDays & (1 << index)) !== 0;
                  return (
                    <Button
                      key={day}
                      type="button"
                      variant={on ? "default" : "outline"}
                      size="sm"
                      onClick={() => setAlertDays(on ? alertDays & ~(1 << index) : alertDays | (1 << index))}
                      className={on ? "bg-cn-brand-blue hover:bg-cn-brand-blue-hover" : ""}
                      data-testid={`button-alert-day-${day.toLowerCase()}`}
                    >
                      {day}
                    </Button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500">
                {alertDays === 0 ? "Pick at least one day." : "Alerts go out on these days only."}
              </p>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Switch checked={alertUseActiveHours} onCheckedChange={setAlertUseActiveHours} data-testid="switch-alert-active-hours" />
                <Label>Only between these hours</Label>
              </div>
              {alertUseActiveHours && (
                <div className="flex items-center gap-2 pl-2">
                  <Input
                    type="time"
                    value={alertStartTime}
                    onChange={(e) => setAlertStartTime(e.target.value)}
                    className="w-32"
                  />
                  <span className="text-sm text-gray-500">to</span>
                  <Input
                    type="time"
                    value={alertEndTime}
                    onChange={(e) => setAlertEndTime(e.target.value)}
                    className="w-32"
                  />
                </div>
              )}
              <p className="text-xs text-gray-500">
                {alertUseActiveHours
                  ? "On the vendor's clock. An overnight span (20:00 to 06:00) counts as the evening's day."
                  : "Off: any hour of an active day."}
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setAlertDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={submitAlert}
              disabled={!alertCanSave || createAlertMutation.isPending || updateAlertMutation.isPending}
              data-testid="button-save-alert-subscriber"
            >
              {editingAlert ? "Save" : "Add"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Product Dialog */}
      <Dialog open={productDialogOpen} onOpenChange={(open) => {
        setProductDialogOpen(open);
        if (!open) {
          // Clear pending image state when dialog closes (cancel or X)
          setPendingImageFile(null);
          setPendingImagePreview(null);
        }
      }}>
        <DialogContent className="max-w-md md:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProduct ? "Edit item" : "Add item"}
            </DialogTitle>
          </DialogHeader>
          {/* One page per item (Jon, 2026-10-14): the item — its name, category and picture,
              shared by all its units — then its units, and the fields of the unit the form
              is open on. Two columns from md up: the fields, and beside them a live
              picture of what is being typed. On a phone the picture sits under the item. */}
          <form onSubmit={productForm.handleSubmit(handleProductSubmit, (errors) => console.error('🚫 RHF validation failed:', errors))} className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,20rem)] md:gap-x-6">
            <div className="space-y-4 md:col-start-1">
            <div>
              <Label htmlFor="product-item" className="flex items-center gap-1">
                Item
                <InfoTooltip inTabOrder={false}>{ITEM_TIP}</InfoTooltip>
              </Label>
              <Input
                id="product-item"
                aria-description={ITEM_TIP}
                maxLength={ITEM_NAME_MAX}
                autoComplete="off"
                {...productForm.register("itemName")}
                placeholder="e.g., Lemons"
                data-testid="input-product-item"
              />
              {productForm.formState.errors.itemName && (
                <p className="text-sm text-red-600">{productForm.formState.errors.itemName.message}</p>
              )}
              {productForm.watch("pack") && (
                <p className="mt-1.5 text-xs text-amber-700" data-testid="text-product-old-pack">
                  The name also carries <span className="font-semibold">{productForm.watch("pack")}</span> from the old Pack box.
                  If it still matters, add it to the item or the unit, then{" "}
                  <button
                    type="button"
                    className="underline"
                    onClick={() => productForm.setValue("pack", "", { shouldDirty: true })}
                    data-testid="button-remove-old-pack"
                  >
                    remove it
                  </button>
                  .
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="product-category">Category</Label>
              <Select
                value={productForm.watch("categoryId") === 0 ? "" : productForm.watch("categoryId").toString()}
                onValueChange={(value) => productForm.setValue("categoryId", parseInt(value))}
              >
                <SelectTrigger data-testid="select-product-category">
                  <SelectValue placeholder="[Select Category]" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category: Category) => (
                    <SelectItem key={category.id} value={category.id.toString()}>
                      <span className="inline-flex items-center gap-1">
                        <CategoryIcon icon={category.icon} size="sm" /> {category.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {productForm.formState.errors.categoryId && (
                <p className="text-sm text-red-600">{productForm.formState.errors.categoryId.message}</p>
              )}
            </div>

            <div>
              <Label>Picture</Label>
              <input
                type="file"
                ref={imageInputRef}
                accept="image/png,image/jpeg,image/gif,image/webp"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    // For both new and existing products: store as pending until Save
                    handleImageUpload(file);
                  }
                  e.target.value = '';
                }}
              />
              <div className="flex items-center space-x-4">
                <Button
                  type="button"
                  variant="ghost"
                  className="p-0 h-auto bg-transparent hover:bg-transparent border-0 shadow-none"
                  disabled={isUploadingImage}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    imageInputRef.current?.click();
                  }}
                >
                  <div className="relative group">
                    {isUploadingImage ? (
                      <div className="w-20 h-20 flex items-center justify-center border rounded bg-gray-50">
                        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                      </div>
                    ) : (
                      <>
                        <img 
                          src={pendingImagePreview || productForm.watch("imagePath") || getProductImage(productFormName({ itemName: productForm.watch("itemName"), pack: productForm.watch("pack"), unit: productForm.watch("unit") }) || "Default", editingProduct)}
                          alt="Product preview"
                          className="w-20 h-20 object-cover rounded border cursor-pointer group-hover:opacity-75 transition-opacity"
                        />
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black bg-opacity-30 rounded">
                          <Camera className="h-6 w-6 text-white drop-shadow-lg" />
                        </div>
                      </>
                    )}
                  </div>
                </Button>
                <div className="flex-1 space-y-2">
                  <p className="text-xs text-gray-500">
                    Click the image to {editingProduct?.id ? "change it" : "add a photo"} (PNG, JPG, GIF, WebP up to 5MB).
                    {editingProduct && unitsOfItem(editingProduct, products).length > 1 && " Every unit of this item shows it."}
                  </p>
                  {pendingImageFile && !editingProduct?.id && (
                    <p className="text-xs text-green-600">
                      Image selected - will be uploaded when you save
                    </p>
                  )}
                </div>
              </div>
            </div>

            </div>

            <aside className="self-start rounded-lg bg-gray-50 p-3 md:sticky md:top-0 md:col-start-2 md:row-span-2 md:row-start-1">
              <ProductNamePreview
                members={productPreviewMembers()}
                photo={pendingImagePreview || productForm.watch("imagePath") || getProductImage(editingProduct?.name || "Default", editingProduct)}
              />
            </aside>

            <div className="space-y-4 md:col-start-1">
            {editingProduct && (
              <ProductUnitsSection
                product={products.find((p: Product) => p.id === editingProduct.id) ?? editingProduct}
                products={products}
                item={productForm.watch("itemName") ?? ""}
                onOpenUnit={openUnit}
              />
            )}

            <div className="space-y-4 rounded-lg border border-gray-200 p-3" data-testid="product-unit-fields">
            {editingProduct && unitsOfItem(editingProduct, products).length > 1 && (
              <p className="text-sm font-medium text-gray-700">
                The {editingProduct.unit ?? "open"} unit
              </p>
            )}
            <div>
              <Label htmlFor="product-unit">Unit</Label>
              <Input
                id="product-unit"
                maxLength={PRODUCT_UNIT_MAX}
                autoComplete="off"
                {...productForm.register("unit")}
                placeholder="e.g., case"
                data-testid="input-product-unit"
              />
              {(() => {
                // No chips for the units already in use (Jon, 2026-10-14). A near-miss of
                // one still gets pointed at the org's own spelling ("lb", not "lbs"): a
                // customer type hiding "lb" and the pick sheet's Unit column both key on
                // the exact unit. Anything typed is still accepted.
                const { inUse } = productUnitChoices(products);
                const existing = existingSpellingOf(productForm.watch("unit") ?? "", inUse);
                if (!existing) return null;
                return (
                  <p className="text-xs text-amber-700 mt-1.5" data-testid="text-product-unit-existing">
                    You already use <span className="font-semibold">{existing.unit}</span> on {existing.count}{" "}
                    {existing.count === 1 ? "product" : "products"}.{" "}
                    <button
                      type="button"
                      className="underline"
                      onClick={() => productForm.setValue("unit", existing.unit, { shouldDirty: true, shouldValidate: true })}
                    >
                      Use {existing.unit}
                    </button>
                    {" "}so they're treated as one unit, or keep what you typed.
                  </p>
                );
              })()}
              <p className="text-xs text-gray-500 mt-1">
                What one quantity means: case, each, 2 lb bag.
              </p>
              <p className="mt-1.5 flex flex-wrap items-center gap-1 text-xs text-gray-500" data-testid="text-product-prints-as">
                Prints as
                <span className="font-medium text-gray-800">
                  {productFormName({ itemName: productForm.watch("itemName"), pack: productForm.watch("pack"), unit: productForm.watch("unit") }) || "…"}
                </span>
                <InfoTooltip inTabOrder={false}>This unit's official name: the item and the unit together. Pick sheets, invoices and your orders page print it.</InfoTooltip>
              </p>
              {productForm.formState.errors.unit && (
                <p className="text-sm text-red-600">{productForm.formState.errors.unit.message}</p>
              )}
            </div>

            <div className="flex gap-3">
              <div className="flex-1">
                <Label htmlFor="product-price">Price (Optional)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                  <Input
                    id="product-price"
                    type="text"
                    inputMode="decimal"
                    {...productForm.register("price")}
                    placeholder="e.g., 4.50"
                    className="pl-7"
                    data-testid="input-product-price"
                  />
                </div>
              </div>
              <div className="w-24">
                <Label htmlFor="product-tax">Tax %</Label>
                <div className="relative">
                  <Input
                    id="product-tax"
                    type="text"
                    inputMode="decimal"
                    {...productForm.register("taxPercent")}
                    placeholder="e.g., 13"
                    className="pr-7"
                    data-testid="input-product-tax"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">%</span>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="product-active"
                checked={productForm.watch("isActive")}
                onCheckedChange={(checked) => productForm.setValue("isActive", checked as boolean)}
                data-testid="checkbox-product-active"
              />
              <Label htmlFor="product-active">Active (customers can order this unit)</Label>
            </div>

            <div>
              <Label htmlFor="product-subheading">Subheading (Optional)</Label>
              <Input
                id="product-subheading"
                {...productForm.register("subheading")}
                placeholder="e.g., Sunkist, organic"
                data-testid="input-product-subheading"
              />
              <p className="text-xs text-gray-500 mt-1">
                Customers see it under the item on your menu. It does not print on pick sheets.
              </p>
              {productForm.formState.errors.subheading && (
                <p className="text-sm text-red-600">{productForm.formState.errors.subheading.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="product-aliases">Also known as (Optional)</Label>
              <Input
                id="product-aliases"
                {...productForm.register("aliases")}
                placeholder="e.g., romas, plum tomatoes"
                data-testid="input-product-aliases"
              />
              <p className="text-xs text-gray-500 mt-1">
                Comma-separated nicknames buyers use. The text-order agent and product search both match them. Up to 20.
              </p>
            </div>

            <ProductCustomerTypesSection
              productId={editingProduct?.id ?? null}
              unit={productForm.watch("unit") ?? ""}
              categoryId={productForm.watch("categoryId") || null}
              categoryName={categories.find((c: Category) => c.id === productForm.watch("categoryId"))?.name ?? null}
              choices={productTypeChoices}
              onChoice={(typeId, shown) => setProductTypeChoices((prev) => ({ ...prev, [typeId]: shown }))}
            />

            {/* QuickBooks Linking - only show when editing existing product and QB is connected */}
            {editingProduct && qbConnected && (() => {
              const linkedItem = editingProduct.quickbooksItemId
                ? qbProductSyncData?.qbItems?.find(i => i.id === editingProduct.quickbooksItemId)
                : null;
              return (
                <div className={`p-3 rounded-lg border ${
                  editingProduct.quickbooksItemId
                    ? "bg-green-50 border-green-200"
                    : "bg-gray-50 border-gray-200"
                }`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <img src={quickbooksLogo} alt="QuickBooks" className="w-7 h-7 rounded flex-shrink-0" />
                      {editingProduct.quickbooksItemId ? (
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <CheckCircle2 className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
                            <p className="font-medium text-green-800 text-sm truncate">
                              {linkedItem ? linkedItem.name : `Item #${editingProduct.quickbooksItemId}`}
                            </p>
                          </div>
                          {linkedItem && (
                            <p className="text-xs text-green-600 ml-5">
                              {linkedItem.type}{linkedItem.unitPrice != null ? ` · $${linkedItem.unitPrice.toFixed(2)}` : ""}
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">Not linked to QuickBooks</p>
                      )}
                    </div>
                    {editingProduct.quickbooksItemId ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => unlinkQBProductMutation.mutate({ productId: editingProduct.id })}
                        disabled={unlinkQBProductMutation.isPending}
                        className="border border-gray-200 text-gray-500 hover:text-red-600 hover:border-red-200 hover:bg-red-50 flex-shrink-0"
                      >
                        {unlinkQBProductMutation.isPending ? "Unlinking…" : "Unlink"}
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setQbProductLinkOpen(true)}
                        className="border-cn-brand-blue text-cn-brand-blue hover:bg-blue-50 flex-shrink-0"
                      >
                        <Link2 className="h-3.5 w-3.5 mr-1.5" />
                        Link to QuickBooks
                      </Button>
                    )}
                  </div>
                </div>
              );
            })()}

            {editingProduct && (
              <div className="border-t pt-4">
                <Button
                  type="button"
                  variant="ghost"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 w-full justify-start"
                  onClick={() => {
                    setProductDialogOpen(false);
                    handleDeleteProduct(editingProduct.id);
                  }}
                  data-testid="button-delete-product-dialog"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {unitsOfItem(editingProduct, products).length > 1 ? `Delete the ${editingProduct.unit ?? "open"} unit` : "Delete item"}
                </Button>
              </div>
            )}

            </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setProductDialogOpen(false);
                  setEditingProduct(null);
                  productForm.reset();
                  setPendingImageFile(null);
                  setPendingImagePreview(null);
                }}
                className="flex-1"
                data-testid="button-cancel-product"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={productForm.formState.isSubmitting}
                className="flex-1 bg-cn-brand-green hover:bg-cn-brand-green-hover"
                data-testid="button-save-product"
              >
                {productForm.formState.isSubmitting 
                  ? "Saving..." 
                  : editingProduct 
                    ? "Save"
                    : "Add item"
                }
              </Button>
            </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* QuickBooks Item Link Dialog */}
      <Dialog open={qbProductLinkOpen} onOpenChange={setQbProductLinkOpen}>
        <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-cn-brand-blue" />
              Link to QuickBooks Item
            </DialogTitle>
            <DialogDescription>
              Search for a QuickBooks item to link to <strong>{editingProduct?.name}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                value={qbProductSearchQuery}
                onChange={(e) => setQbProductSearchQuery(e.target.value)}
                placeholder="Search by item name..."
                className="pl-9"
                autoFocus
              />
            </div>

            <div className="space-y-2 max-h-80 overflow-y-auto">
              {qbProductsLoading ? (
                <div className="flex items-center justify-center py-8 text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Loading QuickBooks items…
                </div>
              ) : (() => {
                const query = qbProductSearchQuery.trim().toLowerCase();
                const allItems = qbProductSyncData?.qbItems ?? [];
                const freshOrderProducts = qbProductSyncData?.freshOrderProducts ?? [];
                const filtered = query.length === 0
                  ? allItems
                  : allItems.filter(item => item.name.toLowerCase().includes(query));

                if (!qbProductSyncData) {
                  return (
                    <div className="text-center py-8 text-sm text-gray-400">
                      Loading…
                    </div>
                  );
                }
                if (filtered.length === 0) {
                  return (
                    <div className="text-center py-8 text-sm text-gray-500">
                      No QuickBooks items found{query ? ` for "${qbProductSearchQuery}"` : ""}
                    </div>
                  );
                }
                return filtered.map((item) => {
                  const conflictProduct = freshOrderProducts.find(
                    p => p.quickbooksItemId === item.id && p.id !== editingProduct?.id
                  );
                  return (
                    <div
                      key={item.id}
                      className={`flex items-center justify-between p-3 border rounded-lg gap-3 ${
                        conflictProduct ? "bg-amber-50 border-amber-200" : "bg-white border-gray-200"
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{item.name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {item.type}{item.unitPrice != null ? ` · $${item.unitPrice.toFixed(2)}` : ""}
                        </p>
                        {conflictProduct && (
                          <div className="flex items-center gap-1 mt-1">
                            <AlertTriangle className="h-3 w-3 text-amber-500 flex-shrink-0" />
                            <p className="text-xs text-amber-700">Already linked to {conflictProduct.name}</p>
                          </div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        disabled={!!conflictProduct || linkQBProductMutation.isPending}
                        onClick={() => editingProduct && linkQBProductMutation.mutate({ productId: editingProduct.id, quickbooksItemId: item.id })}
                        className={conflictProduct ? "" : "bg-cn-brand-green hover:bg-cn-brand-green-hover text-white flex-shrink-0"}
                      >
                        Link
                      </Button>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? "Edit Category" : "Add New Category"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={categoryForm.handleSubmit(handleCategorySubmit, (errors) => console.error('🚫 RHF validation failed:', errors))} className="space-y-4">
            {editingCategory?.isSystemCategory && (
              <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <img src={quickbooksLogo} alt="QuickBooks" className="w-10 h-10 rounded" />
                <div>
                  <p className="font-medium text-green-800">QuickBooks Import Category</p>
                  <p className="text-xs text-green-600">This category cannot be renamed, deleted, or made visible to customers.</p>
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="category-name">Category Name</Label>
              <Input
                id="category-name"
                {...categoryForm.register("name")}
                placeholder="e.g., Vegetables"
                disabled={editingCategory?.isSystemCategory}
                data-testid="input-category-name"
              />
              {categoryForm.formState.errors.name && (
                <p className="text-sm text-red-600">{categoryForm.formState.errors.name.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="category-icon">Category Icon/Emoji</Label>
              {editingCategory?.isSystemCategory ? (
                <div className="flex items-center gap-2 p-2 border rounded bg-gray-50">
                  <img src={quickbooksLogo} alt="QuickBooks" className="w-6 h-6 rounded" />
                  <span className="text-sm text-gray-500">QuickBooks logo (cannot be changed)</span>
                </div>
              ) : (
                <Select
                  value={categoryForm.watch("icon")}
                  onValueChange={(value) => categoryForm.setValue("icon", value)}
                >
                <SelectTrigger id="category-icon" data-testid="select-category-icon">
                  <SelectValue placeholder="Select an emoji" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {getCategoryIconOptions().map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <span className="inline-flex items-center gap-1">
                        <CategoryIcon icon={option.value} size="sm" /> {option.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
                </Select>
              )}
              {!editingCategory?.isSystemCategory && categoryForm.formState.errors.icon && (
                <p className="text-sm text-red-600">{categoryForm.formState.errors.icon.message}</p>
              )}
            </div>

            {!editingCategory?.isSystemCategory && (
              <>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="category-visible"
                    checked={categoryForm.watch("isVisibleToCustomers")}
                    onCheckedChange={(checked) => categoryForm.setValue("isVisibleToCustomers", checked === true)}
                    data-testid="checkbox-category-visible"
                  />
                  <Label htmlFor="category-visible" className="text-sm font-normal cursor-pointer">
                    Visible to customers
                  </Label>
                </div>
                <p className="text-xs text-gray-500 -mt-2">
                  Uncheck to hide this category from customer order pages
                </p>
              </>
            )}

            {editingCategory && !editingCategory.isSystemCategory && (
              <div className="border-t pt-4">
                <Button
                  type="button"
                  variant="ghost"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 w-full justify-start"
                  onClick={() => {
                    setCategoryDialogOpen(false);
                    setEditingCategory(null);
                    categoryForm.reset();
                    handleDeleteCategory(editingCategory.id);
                  }}
                  data-testid="button-delete-category-dialog"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Category
                </Button>
              </div>
            )}

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setCategoryDialogOpen(false);
                  setEditingCategory(null);
                  categoryForm.reset();
                }}
                className="flex-1"
                data-testid="button-cancel-category"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={categoryForm.formState.isSubmitting}
                className="flex-1 bg-cn-brand-green hover:bg-cn-brand-green-hover"
                data-testid="button-save-category"
              >
                {categoryForm.formState.isSubmitting 
                  ? "Saving..." 
                  : editingCategory 
                    ? "Update Category" 
                    : "Add Category"
                }
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* User Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingUser ? "Edit User" : "Add New User"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={userForm.handleSubmit(handleUserSubmit, (errors) => console.error('🚫 RHF validation failed:', errors))} className="space-y-4">
            <div>
              <Label htmlFor="user-username">Display Name</Label>
              <Input
                id="user-username"
                {...userForm.register("username")}
                placeholder="e.g., John Smith"
                data-testid="input-user-username"
              />
              {userForm.formState.errors.username && (
                <p className="text-sm text-red-600">{userForm.formState.errors.username.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="user-email">Email</Label>
              <Input
                id="user-email"
                type="email"
                {...userForm.register("email")}
                placeholder="e.g., john@example.com"
                data-testid="input-user-email"
              />
              <p className="text-xs text-gray-500 mt-1">Used for login</p>
              {userForm.formState.errors.email && (
                <p className="text-sm text-red-600">{userForm.formState.errors.email.message}</p>
              )}
            </div>

            {!editingUser && (
              <div>
                <Label htmlFor="user-password">Password</Label>
                <Input
                  id="user-password"
                  type="password"
                  {...userForm.register("password")}
                  placeholder="At least 6 characters"
                  data-testid="input-user-password"
                />
                {userForm.formState.errors.password && (
                  <p className="text-sm text-red-600">{userForm.formState.errors.password.message}</p>
                )}
              </div>
            )}

            <div>
              <Label htmlFor="user-role">Role</Label>
              <Select
                value={userForm.watch("role")}
                onValueChange={(value) => userForm.setValue("role", value as "admin" | "staff")}
              >
                <SelectTrigger data-testid="select-user-role">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                </SelectContent>
              </Select>
              {userForm.formState.errors.role && (
                <p className="text-sm text-red-600">{userForm.formState.errors.role.message}</p>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="user-active"
                checked={userForm.watch("isActive")}
                onCheckedChange={(checked) => userForm.setValue("isActive", checked)}
                data-testid="switch-user-active"
              />
              <Label htmlFor="user-active">Active User</Label>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setUserDialogOpen(false);
                  setEditingUser(null);
                  userForm.reset();
                }}
                className="flex-1"
                data-testid="button-cancel-user"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={userForm.formState.isSubmitting}
                className="flex-1 bg-cn-brand-green hover:bg-cn-brand-green-hover"
                data-testid="button-save-user"
              >
                {userForm.formState.isSubmitting 
                  ? "Saving..." 
                  : editingUser 
                    ? "Update User" 
                    : "Add User"
                }
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={passwordResetDialogOpen} onOpenChange={setPasswordResetDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Reset password for user: {resetPasswordUser?.username}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={passwordResetForm.handleSubmit(handlePasswordResetSubmit, (errors) => console.error('🚫 RHF validation failed:', errors))} className="space-y-4">
            <div>
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                {...passwordResetForm.register("newPassword")}
                placeholder="Enter new password"
                data-testid="input-new-password"
              />
              {passwordResetForm.formState.errors.newPassword && (
                <p className="text-sm text-red-600">{passwordResetForm.formState.errors.newPassword.message}</p>
              )}
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setPasswordResetDialogOpen(false);
                  setResetPasswordUser(null);
                  passwordResetForm.reset();
                }}
                className="flex-1"
                data-testid="button-cancel-password-reset"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={passwordResetForm.formState.isSubmitting}
                className="flex-1 bg-cn-brand-green hover:bg-cn-brand-green-hover"
                data-testid="button-save-password-reset"
              >
                {passwordResetForm.formState.isSubmitting
                  ? "Resetting..."
                  : "Reset Password"
                }
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={deactivateConfirmState.open}
        onOpenChange={(open) => {
          if (!open && !deactivateProductMutation.isPending) {
            setDeactivateConfirmState({ open: false, productId: null, productName: '', itemCount: 0 });
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Can't delete this product</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deactivateConfirmState.productName}</strong> has been used on{' '}
              {deactivateConfirmState.itemCount} past order{deactivateConfirmState.itemCount === 1 ? '' : 's'}{' '}
              and can't be deleted because that would break order history.
              <br /><br />
              Would you like to deactivate it instead? It will be hidden from customers but the order history stays intact. You can reactivate it later from the active checkbox.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deactivateProductMutation.isPending}>Keep as is</AlertDialogCancel>
            <AlertDialogAction
              disabled={deactivateProductMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (deactivateConfirmState.productId !== null) {
                  deactivateProductMutation.mutate(deactivateConfirmState.productId);
                }
              }}
            >
              {deactivateProductMutation.isPending ? 'Deactivating…' : 'Deactivate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}